import { defaultsDeep } from 'lodash-es';
import { languages } from '@codemirror/language-data';
import { oneDark } from '@codemirror/theme-one-dark';
import { diffComponentConfig, diffComponent } from '@milkdown/kit/component/diff';
import { diffPluginKey, clearDiffReviewCmd, acceptAllDiffsCmd, diffConfig, diff } from '@milkdown/kit/plugin/diff';
import { streamingPluginKey, startStreamingCmd, abortStreamingCmd, pushChunkCmd, endStreamingCmd, streamingConfig, streaming } from '@milkdown/kit/plugin/streaming';
import { createSlice } from '@milkdown/kit/ctx';
import { editorViewCtx, serializerCtx, commandsCtx, schemaCtx, editorCtx, EditorStatus, Editor, rootCtx, defaultValueCtx, editorViewOptionsCtx } from '@milkdown/kit/core';
import { aiBuildContextError, aiProviderError } from '@milkdown/kit/exception';
import { $ctx, $command, $prose, $nodeSchema, $inputRule, $remark, getMarkdown } from '@milkdown/kit/utils';
import { TextSelection, PluginKey, Plugin, NodeSelection, EditorState } from '@milkdown/kit/prose/state';
import DOMPurify from 'dompurify';
import { TooltipProvider, tooltipFactory } from '@milkdown/kit/plugin/tooltip';
import { posToDOMRect, findParent, findNodeInSelection, nodeRule } from '@milkdown/kit/prose';
import { defineComponent, ref, watch, computed, h, nextTick, createApp, watchEffect, onUnmounted, Fragment, shallowRef, onMounted } from 'vue';
import { Icon } from '@milkdown/kit/component';
import { DecorationSet, Decoration, EditorView } from '@milkdown/kit/prose/view';
import { blockConfig, block, BlockProvider } from '@milkdown/kit/plugin/block';
import { paragraphSchema, clearTextInCurrentBlockCommand, setBlockTypeCommand, headingSchema, blockquoteSchema, wrapInBlockTypeCommand, hrSchema, addBlockTypeCommand, bulletListSchema, orderedListSchema, listItemSchema, codeBlockSchema, selectTextNearPosCommand, toggleStrongCommand, isMarkSelectedCommand, strongSchema, toggleEmphasisCommand, emphasisSchema, toggleInlineCodeCommand, inlineCodeSchema, isNodeSelectedCommand, linkSchema, commonmark } from '@milkdown/kit/preset/commonmark';
import { slashFactory, SlashProvider } from '@milkdown/kit/plugin/slash';
import { imageBlockSchema, imageBlockConfig, imageBlockComponent } from '@milkdown/kit/component/image-block';
import { createTable, toggleStrikethroughCommand, strikethroughSchema, gfm } from '@milkdown/kit/preset/gfm';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { keymap } from '@codemirror/view';
import { codeBlockConfig, codeBlockComponent } from '@milkdown/kit/component/code-block';
import { basicSetup } from 'codemirror';
import { dropIndicatorConfig, cursor as cursor$1 } from '@milkdown/kit/plugin/cursor';
import { createVirtualCursor } from 'prosemirror-virtual-cursor';
import { inlineImageConfig, imageInlineComponent } from '@milkdown/kit/component/image-inline';
import katex from 'katex';
import { redo, undo } from '@milkdown/kit/prose/history';
import { keymap as keymap$1 } from '@milkdown/kit/prose/keymap';
import { Schema } from '@milkdown/kit/prose/model';
import { textblockTypeInputRule } from '@milkdown/kit/prose/inputrules';
import remarkMath from 'remark-math';
import { visit } from 'unist-util-visit';
import { configureLinkTooltip, linkTooltipConfig, linkTooltipPlugin, toggleLinkCommand } from '@milkdown/kit/component/link-tooltip';
import { listItemBlockComponent, listItemBlockConfig } from '@milkdown/kit/component/list-item-block';
import { tableBlockConfig, tableBlock } from '@milkdown/kit/component/table-block';
import clsx from 'clsx';
import { clipboard } from '@milkdown/kit/plugin/clipboard';
import { history } from '@milkdown/kit/plugin/history';
import { indentConfig, indent } from '@milkdown/kit/plugin/indent';
import { listenerCtx, listener } from '@milkdown/kit/plugin/listener';
import { trailing } from '@milkdown/kit/plugin/trailing';
import { uploadConfig, upload } from '@milkdown/kit/plugin/upload';

var CrepeFeature = /* @__PURE__ */ ((CrepeFeature2) => {
  CrepeFeature2["CodeMirror"] = "code-mirror";
  CrepeFeature2["ListItem"] = "list-item";
  CrepeFeature2["LinkTooltip"] = "link-tooltip";
  CrepeFeature2["Cursor"] = "cursor";
  CrepeFeature2["ImageBlock"] = "image-block";
  CrepeFeature2["BlockEdit"] = "block-edit";
  CrepeFeature2["Toolbar"] = "toolbar";
  CrepeFeature2["Placeholder"] = "placeholder";
  CrepeFeature2["Table"] = "table";
  CrepeFeature2["Latex"] = "latex";
  CrepeFeature2["TopBar"] = "top-bar";
  CrepeFeature2["AI"] = "ai";
  return CrepeFeature2;
})(CrepeFeature || {});
const defaultFeatures = {
  ["cursor" /* Cursor */]: true,
  ["list-item" /* ListItem */]: true,
  ["link-tooltip" /* LinkTooltip */]: true,
  ["image-block" /* ImageBlock */]: true,
  ["block-edit" /* BlockEdit */]: true,
  ["placeholder" /* Placeholder */]: true,
  ["toolbar" /* Toolbar */]: true,
  ["code-mirror" /* CodeMirror */]: true,
  ["table" /* Table */]: true,
  ["latex" /* Latex */]: true,
  ["top-bar" /* TopBar */]: false,
  ["ai" /* AI */]: false
};

const aiIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25zm0 14l1.25-2.75L23 19l-2.75-1.25L19 15l-1.25 2.75L15 19l2.75 1.25zM9 20l3-6.5L18 10l-6-3.5L9 0L6 6.5L0 10l6 3.5z"
      transform="translate(3 3) scale(0.75)"
    />
  </svg>
`;

const alignCenterIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M4.25 20.5C4.0375 20.5 3.85942 20.4281 3.71575 20.2843C3.57192 20.1404 3.5 19.9622 3.5 19.7498C3.5 19.5371 3.57192 19.359 3.71575 19.2155C3.85942 19.0718 4.0375 19 4.25 19H19.75C19.9625 19 20.1406 19.0719 20.2843 19.2158C20.4281 19.3596 20.5 19.5378 20.5 19.7502C20.5 19.9629 20.4281 20.141 20.2843 20.2845C20.1406 20.4282 19.9625 20.5 19.75 20.5H4.25ZM8.25 16.625C8.0375 16.625 7.85942 16.5531 7.71575 16.4093C7.57192 16.2654 7.5 16.0872 7.5 15.8748C7.5 15.6621 7.57192 15.484 7.71575 15.3405C7.85942 15.1968 8.0375 15.125 8.25 15.125H15.75C15.9625 15.125 16.1406 15.1969 16.2843 15.3408C16.4281 15.4846 16.5 15.6628 16.5 15.8753C16.5 16.0879 16.4281 16.266 16.2843 16.4095C16.1406 16.5532 15.9625 16.625 15.75 16.625H8.25ZM4.25 12.75C4.0375 12.75 3.85942 12.6781 3.71575 12.5343C3.57192 12.3904 3.5 12.2122 3.5 11.9998C3.5 11.7871 3.57192 11.609 3.71575 11.4655C3.85942 11.3218 4.0375 11.25 4.25 11.25H19.75C19.9625 11.25 20.1406 11.3219 20.2843 11.4658C20.4281 11.6096 20.5 11.7878 20.5 12.0003C20.5 12.2129 20.4281 12.391 20.2843 12.5345C20.1406 12.6782 19.9625 12.75 19.75 12.75H4.25ZM8.25 8.875C8.0375 8.875 7.85942 8.80308 7.71575 8.65925C7.57192 8.51542 7.5 8.33725 7.5 8.12475C7.5 7.91208 7.57192 7.734 7.71575 7.5905C7.85942 7.44683 8.0375 7.375 8.25 7.375H15.75C15.9625 7.375 16.1406 7.44692 16.2843 7.59075C16.4281 7.73458 16.5 7.91275 16.5 8.12525C16.5 8.33792 16.4281 8.516 16.2843 8.6595C16.1406 8.80317 15.9625 8.875 15.75 8.875H8.25ZM4.25 5C4.0375 5 3.85942 4.92808 3.71575 4.78425C3.57192 4.64042 3.5 4.46225 3.5 4.24975C3.5 4.03708 3.57192 3.859 3.71575 3.7155C3.85942 3.57183 4.0375 3.5 4.25 3.5H19.75C19.9625 3.5 20.1406 3.57192 20.2843 3.71575C20.4281 3.85958 20.5 4.03775 20.5 4.25025C20.5 4.46292 20.4281 4.641 20.2843 4.7845C20.1406 4.92817 19.9625 5 19.75 5H4.25Z"
    />
  </svg>
`;

const alignLeftIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M4.25 20.5C4.0375 20.5 3.85942 20.4281 3.71575 20.2843C3.57192 20.1404 3.5 19.9622 3.5 19.7498C3.5 19.5371 3.57192 19.359 3.71575 19.2155C3.85942 19.0718 4.0375 19 4.25 19H19.75C19.9625 19 20.1406 19.0719 20.2843 19.2158C20.4281 19.3596 20.5 19.5378 20.5 19.7502C20.5 19.9629 20.4281 20.141 20.2843 20.2845C20.1406 20.4282 19.9625 20.5 19.75 20.5H4.25ZM4.25 16.625C4.0375 16.625 3.85942 16.5531 3.71575 16.4093C3.57192 16.2654 3.5 16.0872 3.5 15.8748C3.5 15.6621 3.57192 15.484 3.71575 15.3405C3.85942 15.1968 4.0375 15.125 4.25 15.125H13.75C13.9625 15.125 14.1406 15.1969 14.2843 15.3408C14.4281 15.4846 14.5 15.6628 14.5 15.8753C14.5 16.0879 14.4281 16.266 14.2843 16.4095C14.1406 16.5532 13.9625 16.625 13.75 16.625H4.25ZM4.25 12.75C4.0375 12.75 3.85942 12.6781 3.71575 12.5343C3.57192 12.3904 3.5 12.2122 3.5 11.9998C3.5 11.7871 3.57192 11.609 3.71575 11.4655C3.85942 11.3218 4.0375 11.25 4.25 11.25H19.75C19.9625 11.25 20.1406 11.3219 20.2843 11.4658C20.4281 11.6096 20.5 11.7878 20.5 12.0003C20.5 12.2129 20.4281 12.391 20.2843 12.5345C20.1406 12.6782 19.9625 12.75 19.75 12.75H4.25ZM4.25 8.875C4.0375 8.875 3.85942 8.80308 3.71575 8.65925C3.57192 8.51542 3.5 8.33725 3.5 8.12475C3.5 7.91208 3.57192 7.734 3.71575 7.5905C3.85942 7.44683 4.0375 7.375 4.25 7.375H13.75C13.9625 7.375 14.1406 7.44692 14.2843 7.59075C14.4281 7.73458 14.5 7.91275 14.5 8.12525C14.5 8.33792 14.4281 8.516 14.2843 8.6595C14.1406 8.80317 13.9625 8.875 13.75 8.875H4.25ZM4.25 5C4.0375 5 3.85942 4.92808 3.71575 4.78425C3.57192 4.64042 3.5 4.46225 3.5 4.24975C3.5 4.03708 3.57192 3.859 3.71575 3.7155C3.85942 3.57183 4.0375 3.5 4.25 3.5H19.75C19.9625 3.5 20.1406 3.57192 20.2843 3.71575C20.4281 3.85958 20.5 4.03775 20.5 4.25025C20.5 4.46292 20.4281 4.641 20.2843 4.7845C20.1406 4.92817 19.9625 5 19.75 5H4.25Z"
    />
  </svg>
`;

const alignRightIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M4.25 5C4.0375 5 3.85942 4.92808 3.71575 4.78425C3.57192 4.64042 3.5 4.46225 3.5 4.24975C3.5 4.03708 3.57192 3.859 3.71575 3.7155C3.85942 3.57183 4.0375 3.5 4.25 3.5H19.75C19.9625 3.5 20.1406 3.57192 20.2843 3.71575C20.4281 3.85958 20.5 4.03775 20.5 4.25025C20.5 4.46292 20.4281 4.641 20.2843 4.7845C20.1406 4.92817 19.9625 5 19.75 5H4.25ZM10.25 8.875C10.0375 8.875 9.85942 8.80308 9.71575 8.65925C9.57192 8.51542 9.5 8.33725 9.5 8.12475C9.5 7.91208 9.57192 7.734 9.71575 7.5905C9.85942 7.44683 10.0375 7.375 10.25 7.375H19.75C19.9625 7.375 20.1406 7.44692 20.2843 7.59075C20.4281 7.73458 20.5 7.91275 20.5 8.12525C20.5 8.33792 20.4281 8.516 20.2843 8.6595C20.1406 8.80317 19.9625 8.875 19.75 8.875H10.25ZM4.25 12.75C4.0375 12.75 3.85942 12.6781 3.71575 12.5343C3.57192 12.3904 3.5 12.2122 3.5 11.9998C3.5 11.7871 3.57192 11.609 3.71575 11.4655C3.85942 11.3218 4.0375 11.25 4.25 11.25H19.75C19.9625 11.25 20.1406 11.3219 20.2843 11.4658C20.4281 11.6096 20.5 11.7878 20.5 12.0003C20.5 12.2129 20.4281 12.391 20.2843 12.5345C20.1406 12.6782 19.9625 12.75 19.75 12.75H4.25ZM10.25 16.625C10.0375 16.625 9.85942 16.5531 9.71575 16.4093C9.57192 16.2654 9.5 16.0872 9.5 15.8748C9.5 15.6621 9.57192 15.484 9.71575 15.3405C9.85942 15.1968 10.0375 15.125 10.25 15.125H19.75C19.9625 15.125 20.1406 15.1969 20.2843 15.3408C20.4281 15.4846 20.5 15.6628 20.5 15.8753C20.5 16.0879 20.4281 16.266 20.2843 16.4095C20.1406 16.5532 19.9625 16.625 19.75 16.625H10.25ZM4.25 20.5C4.0375 20.5 3.85942 20.4281 3.71575 20.2843C3.57192 20.1404 3.5 19.9622 3.5 19.7498C3.5 19.5371 3.57192 19.359 3.71575 19.2155C3.85942 19.0718 4.0375 19 4.25 19H19.75C19.9625 19 20.1406 19.0719 20.2843 19.2158C20.4281 19.3596 20.5 19.5378 20.5 19.7502C20.5 19.9629 20.4281 20.141 20.2843 20.2845C20.1406 20.4282 19.9625 20.5 19.75 20.5H4.25Z"
    />
  </svg>
`;

const boldIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M8.85758 18.625C8.4358 18.625 8.07715 18.4772 7.78163 18.1817C7.48613 17.8862 7.33838 17.5275 7.33838 17.1058V6.8942C7.33838 6.47242 7.48613 6.11377 7.78163 5.81825C8.07715 5.52275 8.4358 5.375 8.85758 5.375H12.1999C13.2191 5.375 14.1406 5.69231 14.9643 6.32693C15.788 6.96154 16.1999 7.81603 16.1999 8.89038C16.1999 9.63779 16.0194 10.2471 15.6585 10.7183C15.2976 11.1894 14.9088 11.5314 14.4922 11.7442C15.005 11.9211 15.4947 12.2708 15.9614 12.7933C16.428 13.3157 16.6614 14.0192 16.6614 14.9038C16.6614 16.182 16.1902 17.1217 15.2479 17.723C14.3056 18.3243 13.3563 18.625 12.3999 18.625H8.85758ZM9.4883 16.6327H12.3191C13.1063 16.6327 13.6627 16.4141 13.9884 15.9769C14.314 15.5397 14.4768 15.1205 14.4768 14.7192C14.4768 14.3179 14.314 13.8987 13.9884 13.4615C13.6627 13.0243 13.0909 12.8057 12.273 12.8057H9.4883V16.6327ZM9.4883 10.875H12.0826C12.6903 10.875 13.172 10.7013 13.5278 10.3539C13.8836 10.0064 14.0615 9.59037 14.0615 9.10575C14.0615 8.59035 13.8733 8.16918 13.497 7.84225C13.1207 7.51533 12.6595 7.35188 12.1133 7.35188H9.4883V10.875Z"
    />
  </svg>
`;

const bulletIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_952_6527)">
      <circle cx="12" cy="12" r="3" />
    </g>
    <defs>
      <clipPath id="clip0_952_6527">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const bulletListIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_8070)">
      <path
        d="M4 10.5C3.17 10.5 2.5 11.17 2.5 12C2.5 12.83 3.17 13.5 4 13.5C4.83 13.5 5.5 12.83 5.5 12C5.5 11.17 4.83 10.5 4 10.5ZM4 4.5C3.17 4.5 2.5 5.17 2.5 6C2.5 6.83 3.17 7.5 4 7.5C4.83 7.5 5.5 6.83 5.5 6C5.5 5.17 4.83 4.5 4 4.5ZM4 16.5C3.17 16.5 2.5 17.18 2.5 18C2.5 18.82 3.18 19.5 4 19.5C4.82 19.5 5.5 18.82 5.5 18C5.5 17.18 4.83 16.5 4 16.5ZM8 19H20C20.55 19 21 18.55 21 18C21 17.45 20.55 17 20 17H8C7.45 17 7 17.45 7 18C7 18.55 7.45 19 8 19ZM8 13H20C20.55 13 21 12.55 21 12C21 11.45 20.55 11 20 11H8C7.45 11 7 11.45 7 12C7 12.55 7.45 13 8 13ZM7 6C7 6.55 7.45 7 8 7H20C20.55 7 21 6.55 21 6C21 5.45 20.55 5 20 5H8C7.45 5 7 5.45 7 6Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_8070">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const captionIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M9 22a1 1 0 0 1-1-1v-3H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6.1l-3.7 3.71c-.2.19-.45.29-.7.29zm1-6v3.08L13.08 16H20V4H4v12z"
    />
  </svg>
`;

const checkBoxCheckedIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_1803_1151)">
      <path
        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10.71 16.29C10.32 16.68 9.69 16.68 9.3 16.29L5.71 12.7C5.32 12.31 5.32 11.68 5.71 11.29C6.1 10.9 6.73 10.9 7.12 11.29L10 14.17L16.88 7.29C17.27 6.9 17.9 6.9 18.29 7.29C18.68 7.68 18.68 8.31 18.29 8.7L10.71 16.29Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_1803_1151">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const checkBoxUncheckedIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_1803_535)">
      <path
        d="M18 19H6C5.45 19 5 18.55 5 18V6C5 5.45 5.45 5 6 5H18C18.55 5 19 5.45 19 6V18C19 18.55 18.55 19 18 19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_1803_535">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const chevronDownIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
    class="w-6 h-6"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
`;

const chevronLeftIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M15.75 19.5L8.25 12l7.5-7.5"
    />
  </svg>
`;

const chevronRightIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
`;

const clearIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_1098_15553)">
      <path
        d="M18.3007 5.70973C17.9107 5.31973 17.2807 5.31973 16.8907 5.70973L12.0007 10.5897L7.1107 5.69973C6.7207 5.30973 6.0907 5.30973 5.7007 5.69973C5.3107 6.08973 5.3107 6.71973 5.7007 7.10973L10.5907 11.9997L5.7007 16.8897C5.3107 17.2797 5.3107 17.9097 5.7007 18.2997C6.0907 18.6897 6.7207 18.6897 7.1107 18.2997L12.0007 13.4097L16.8907 18.2997C17.2807 18.6897 17.9107 18.6897 18.3007 18.2997C18.6907 17.9097 18.6907 17.2797 18.3007 16.8897L13.4107 11.9997L18.3007 7.10973C18.6807 6.72973 18.6807 6.08973 18.3007 5.70973Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_1098_15553">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const codeIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_8081)">
      <path
        d="M9.4 16.6L4.8 12L9.4 7.4L8 6L2 12L8 18L9.4 16.6ZM14.6 16.6L19.2 12L14.6 7.4L16 6L22 12L16 18L14.6 16.6Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_8081">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const codeBlockIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h16V5H4zm8 10h6v2h-6v-2zm-3.333-3L5.838 9.172l1.415-1.415L11.495 12l-4.242 4.243-1.415-1.415L8.667 12z"
    />
  </svg>
`;

const confirmIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <g clip-path="url(#clip0_1013_1606)">
      <path
        d="M9.00012 16.1998L5.50012 12.6998C5.11012 12.3098 4.49012 12.3098 4.10012 12.6998C3.71012 13.0898 3.71012 13.7098 4.10012 14.0998L8.29012 18.2898C8.68012 18.6798 9.31012 18.6798 9.70012 18.2898L20.3001 7.69982C20.6901 7.30982 20.6901 6.68982 20.3001 6.29982C19.9101 5.90982 19.2901 5.90982 18.9001 6.29982L9.00012 16.1998Z"
        fill="#817567"
      />
    </g>
    <defs>
      <clipPath id="clip0_1013_1606">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const copyIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height="24px"
    viewBox="0 -960 960 960"
    width="24px"
    fill="none"
  >
    <path
      d="M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z"
    />
  </svg>
`;

const dividerIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_7900)">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M19 13H5C4.45 13 4 12.55 4 12C4 11.45 4.45 11 5 11H19C19.55 11 20 11.45 20 12C20 12.55 19.55 13 19 13Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_7900">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const dragHandleIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
  >
    <path
      d="M3.5 9.83366C3.35833 9.83366 3.23961 9.78571 3.14383 9.68983C3.04794 9.59394 3 9.47516 3 9.33349C3 9.19171 3.04794 9.07299 3.14383 8.97733C3.23961 8.88155 3.35833 8.83366 3.5 8.83366H12.5C12.6417 8.83366 12.7604 8.8816 12.8562 8.97749C12.9521 9.07338 13 9.19216 13 9.33383C13 9.4756 12.9521 9.59433 12.8562 9.68999C12.7604 9.78577 12.6417 9.83366 12.5 9.83366H3.5ZM3.5 7.16699C3.35833 7.16699 3.23961 7.11905 3.14383 7.02316C3.04794 6.92727 3 6.80849 3 6.66683C3 6.52505 3.04794 6.40633 3.14383 6.31066C3.23961 6.21488 3.35833 6.16699 3.5 6.16699H12.5C12.6417 6.16699 12.7604 6.21494 12.8562 6.31083C12.9521 6.40671 13 6.52549 13 6.66716C13 6.80894 12.9521 6.92766 12.8562 7.02333C12.7604 7.1191 12.6417 7.16699 12.5 7.16699H3.5Z"
    />
  </svg>
`;

const editIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_1013_1585)">
      <path
        d="M14.06 9.02L14.98 9.94L5.92 19H5V18.08L14.06 9.02ZM17.66 3C17.41 3 17.15 3.1 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C18.17 3.09 17.92 3 17.66 3ZM14.06 6.19L3 17.25V21H6.75L17.81 9.94L14.06 6.19Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_1013_1585">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const enterKeyIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M9 16l-5-5l5-5l1.4 1.4L7.825 10H18V6h2v6H7.825l2.575 2.6z"
    />
  </svg>
`;

const grammarCheckIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M9.55 17.575L4.225 12.25l1.4-1.425L9.55 14.75l8.825-8.825l1.4 1.425z"
    />
  </svg>
`;

const h1Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_992_5553)">
      <path
        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM12 17H14V7H10V9H12V17Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_992_5553">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const h2Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_992_5559)">
      <path
        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM15 15H11V13H13C14.1 13 15 12.11 15 11V9C15 7.89 14.1 7 13 7H9V9H13V11H11C9.9 11 9 11.89 9 13V17H15V15Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_992_5559">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const h3Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_992_5565)">
      <path
        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM15 15V13.5C15 12.67 14.33 12 13.5 12C14.33 12 15 11.33 15 10.5V9C15 7.89 14.1 7 13 7H9V9H13V11H11V13H13V15H9V17H13C14.1 17 15 16.11 15 15Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_992_5565">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const h4Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_7757)">
      <path
        d="M19.04 3H5.04004C3.94004 3 3.04004 3.9 3.04004 5V19C3.04004 20.1 3.94004 21 5.04004 21H19.04C20.14 21 21.04 20.1 21.04 19V5C21.04 3.9 20.14 3 19.04 3ZM19.04 19H5.04004V5H19.04V19ZM13.04 17H15.04V7H13.04V11H11.04V7H9.04004V13H13.04V17Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_7757">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const h5Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_7760)">
      <path
        d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM15 15V13C15 11.89 14.1 11 13 11H11V9H15V7H9V13H13V15H9V17H13C14.1 17 15 16.11 15 15Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_7760">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const h6Icon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_7763)">
      <path
        d="M11 17H13C14.1 17 15 16.11 15 15V13C15 11.89 14.1 11 13 11H11V9H15V7H11C9.9 7 9 7.89 9 9V15C9 16.11 9.9 17 11 17ZM11 13H13V15H11V13ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_7763">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const imageIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_8075)">
      <path
        d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM14.14 11.86L11.14 15.73L9 13.14L6 17H18L14.14 11.86Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_8075">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const italicIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M6.29811 18.625C6.04505 18.625 5.83115 18.5375 5.65641 18.3626C5.48166 18.1877 5.39429 17.9736 5.39429 17.7203C5.39429 17.467 5.48166 17.2532 5.65641 17.0788C5.83115 16.9045 6.04505 16.8173 6.29811 16.8173H9.21159L12.452 7.18265H9.53851C9.28545 7.18265 9.07155 7.0952 8.89681 6.9203C8.72206 6.7454 8.63469 6.5313 8.63469 6.278C8.63469 6.02472 8.72206 5.81089 8.89681 5.63652C9.07155 5.46217 9.28545 5.375 9.53851 5.375H16.8847C17.1377 5.375 17.3516 5.46245 17.5264 5.63735C17.7011 5.81225 17.7885 6.02634 17.7885 6.27962C17.7885 6.53293 17.7011 6.74676 17.5264 6.92113C17.3516 7.09548 17.1377 7.18265 16.8847 7.18265H14.2789L11.0385 16.8173H13.6443C13.8973 16.8173 14.1112 16.9048 14.286 17.0797C14.4607 17.2546 14.5481 17.4687 14.5481 17.722C14.5481 17.9752 14.4607 18.1891 14.286 18.3634C14.1112 18.5378 13.8973 18.625 13.6443 18.625H6.29811Z"
    />
  </svg>
`;

const linkIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M17.0385 19.5003V16.5388H14.0769V15.0388H17.0385V12.0773H18.5384V15.0388H21.5V16.5388H18.5384V19.5003H17.0385ZM10.8077 16.5388H7.03845C5.78282 16.5388 4.7125 16.0963 3.8275 15.2114C2.9425 14.3266 2.5 13.2564 2.5 12.0009C2.5 10.7454 2.9425 9.67504 3.8275 8.78979C4.7125 7.90454 5.78282 7.46191 7.03845 7.46191H10.8077V8.96186H7.03845C6.1987 8.96186 5.48235 9.25834 4.8894 9.85129C4.29645 10.4442 3.99998 11.1606 3.99998 12.0003C3.99998 12.8401 4.29645 13.5564 4.8894 14.1494C5.48235 14.7423 6.1987 15.0388 7.03845 15.0388H10.8077V16.5388ZM8.25 12.7503V11.2504H15.75V12.7503H8.25ZM21.5 12.0003H20C20 11.1606 19.7035 10.4442 19.1106 9.85129C18.5176 9.25834 17.8013 8.96186 16.9615 8.96186H13.1923V7.46191H16.9615C18.2171 7.46191 19.2875 7.90441 20.1725 8.78939C21.0575 9.67439 21.5 10.7447 21.5 12.0003Z"
    />
  </svg>
`;

const longerIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M3 18v-2h13v2zm0-5v-2h18v2zm0-5V6h18v2zm15 13v-3h-3v-2h3v-3h2v3h3v2h-3v3z"
    />
  </svg>
`;

const menuIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_971_7680)">
      <path
        d="M11 18C11 19.1 10.1 20 9 20C7.9 20 7 19.1 7 18C7 16.9 7.9 16 9 16C10.1 16 11 16.9 11 18ZM9 10C7.9 10 7 10.9 7 12C7 13.1 7.9 14 9 14C10.1 14 11 13.1 11 12C11 10.9 10.1 10 9 10ZM9 4C7.9 4 7 4.9 7 6C7 7.1 7.9 8 9 8C10.1 8 11 7.1 11 6C11 4.9 10.1 4 9 4ZM15 8C16.1 8 17 7.1 17 6C17 4.9 16.1 4 15 4C13.9 4 13 4.9 13 6C13 7.1 13.9 8 15 8ZM15 10C13.9 10 13 10.9 13 12C13 13.1 13.9 14 15 14C16.1 14 17 13.1 17 12C17 10.9 16.1 10 15 10ZM15 16C13.9 16 13 16.9 13 18C13 19.1 13.9 20 15 20C16.1 20 17 19.1 17 18C17 16.9 16.1 16 15 16Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_971_7680">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const orderedListIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_8067)">
      <path
        d="M8 7H20C20.55 7 21 6.55 21 6C21 5.45 20.55 5 20 5H8C7.45 5 7 5.45 7 6C7 6.55 7.45 7 8 7ZM20 17H8C7.45 17 7 17.45 7 18C7 18.55 7.45 19 8 19H20C20.55 19 21 18.55 21 18C21 17.45 20.55 17 20 17ZM20 11H8C7.45 11 7 11.45 7 12C7 12.55 7.45 13 8 13H20C20.55 13 21 12.55 21 12C21 11.45 20.55 11 20 11ZM4.5 16H2.5C2.22 16 2 16.22 2 16.5C2 16.78 2.22 17 2.5 17H4V17.5H3.5C3.22 17.5 3 17.72 3 18C3 18.28 3.22 18.5 3.5 18.5H4V19H2.5C2.22 19 2 19.22 2 19.5C2 19.78 2.22 20 2.5 20H4.5C4.78 20 5 19.78 5 19.5V16.5C5 16.22 4.78 16 4.5 16ZM2.5 5H3V7.5C3 7.78 3.22 8 3.5 8C3.78 8 4 7.78 4 7.5V4.5C4 4.22 3.78 4 3.5 4H2.5C2.22 4 2 4.22 2 4.5C2 4.78 2.22 5 2.5 5ZM4.5 10H2.5C2.22 10 2 10.22 2 10.5C2 10.78 2.22 11 2.5 11H3.8L2.12 12.96C2.04 13.05 2 13.17 2 13.28V13.5C2 13.78 2.22 14 2.5 14H4.5C4.78 14 5 13.78 5 13.5C5 13.22 4.78 13 4.5 13H3.2L4.88 11.04C4.96 10.95 5 10.83 5 10.72V10.5C5 10.22 4.78 10 4.5 10Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_8067">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const plusIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_971_7676)">
      <path
        d="M18 13H13V18C13 18.55 12.55 19 12 19C11.45 19 11 18.55 11 18V13H6C5.45 13 5 12.55 5 12C5 11.45 5.45 11 6 11H11V6C11 5.45 11.45 5 12 5C12.55 5 13 5.45 13 6V11H18C18.55 11 19 11.45 19 12C19 12.55 18.55 13 18 13Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_971_7676">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const quoteIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_7897)">
      <path
        d="M7.17 17C7.68 17 8.15 16.71 8.37 16.26L9.79 13.42C9.93 13.14 10 12.84 10 12.53V8C10 7.45 9.55 7 9 7H5C4.45 7 4 7.45 4 8V12C4 12.55 4.45 13 5 13H7L5.97 15.06C5.52 15.95 6.17 17 7.17 17ZM17.17 17C17.68 17 18.15 16.71 18.37 16.26L19.79 13.42C19.93 13.14 20 12.84 20 12.53V8C20 7.45 19.55 7 19 7H15C14.45 7 14 7.45 14 8V12C14 12.55 14.45 13 15 13H17L15.97 15.06C15.52 15.95 16.17 17 17.17 17Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_7897">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const removeIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M7.30775 20.4997C6.81058 20.4997 6.385 20.3227 6.031 19.9687C5.677 19.6147 5.5 19.1892 5.5 18.692V5.99973H5.25C5.0375 5.99973 4.85942 5.92782 4.71575 5.78398C4.57192 5.64015 4.5 5.46198 4.5 5.24948C4.5 5.03682 4.57192 4.85873 4.71575 4.71523C4.85942 4.57157 5.0375 4.49973 5.25 4.49973H9C9 4.2549 9.08625 4.04624 9.25875 3.87374C9.43108 3.7014 9.63967 3.61523 9.8845 3.61523H14.1155C14.3603 3.61523 14.5689 3.7014 14.7413 3.87374C14.9138 4.04624 15 4.2549 15 4.49973H18.75C18.9625 4.49973 19.1406 4.57165 19.2843 4.71548C19.4281 4.85932 19.5 5.03748 19.5 5.24998C19.5 5.46265 19.4281 5.64073 19.2843 5.78423C19.1406 5.9279 18.9625 5.99973 18.75 5.99973H18.5V18.692C18.5 19.1892 18.323 19.6147 17.969 19.9687C17.615 20.3227 17.1894 20.4997 16.6923 20.4997H7.30775ZM17 5.99973H7V18.692C7 18.7818 7.02883 18.8556 7.0865 18.9132C7.14417 18.9709 7.21792 18.9997 7.30775 18.9997H16.6923C16.7821 18.9997 16.8558 18.9709 16.9135 18.9132C16.9712 18.8556 17 18.7818 17 18.692V5.99973ZM10.1543 16.9997C10.3668 16.9997 10.5448 16.9279 10.6885 16.7842C10.832 16.6404 10.9037 16.4622 10.9037 16.2497V8.74973C10.9037 8.53723 10.8318 8.35907 10.688 8.21523C10.5443 8.07157 10.3662 7.99973 10.1535 7.99973C9.941 7.99973 9.76292 8.07157 9.61925 8.21523C9.47575 8.35907 9.404 8.53723 9.404 8.74973V16.2497C9.404 16.4622 9.47583 16.6404 9.6195 16.7842C9.76333 16.9279 9.94158 16.9997 10.1543 16.9997ZM13.8465 16.9997C14.059 16.9997 14.2371 16.9279 14.3807 16.7842C14.5243 16.6404 14.596 16.4622 14.596 16.2497V8.74973C14.596 8.53723 14.5242 8.35907 14.3805 8.21523C14.2367 8.07157 14.0584 7.99973 13.8458 7.99973C13.6333 7.99973 13.4552 8.07157 13.3115 8.21523C13.168 8.35907 13.0962 8.53723 13.0962 8.74973V16.2497C13.0962 16.4622 13.1682 16.6404 13.312 16.7842C13.4557 16.9279 13.6338 16.9997 13.8465 16.9997Z"
    />
  </svg>
`;

const retryIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M12 20q-3.35 0-5.675-2.325T4 12q0-3.35 2.325-5.675T12 4q1.725 0 3.3.713T18 6.75V4h2v7h-7V9h4.2q-.8-1.4-2.187-2.2T12 6Q9.5 6 7.75 7.75T6 12q0 2.5 1.75 4.25T12 18q1.925 0 3.475-1.1T17.65 14h2.1q-.7 2.65-2.85 4.325T12 20"
    />
  </svg>
`;

const searchIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
    class="w-6 h-6"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
    />
  </svg>
`;

const sendIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M11 20V7.825l-5.6 5.6L4 12l8-8l8 8l-1.4 1.425l-5.6-5.6V20z"
    />
  </svg>
`;

const sendPromptIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M3 20v-6l8-2l-8-2V4l19 8z"
    />
  </svg>
`;

const shorterIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M5 13v-2h14v2z"
    />
  </svg>
`;

const strikethroughIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M3.25 13.7404C3.0375 13.7404 2.85938 13.6684 2.71563 13.5246C2.57188 13.3808 2.5 13.2026 2.5 12.99C2.5 12.7774 2.57188 12.5993 2.71563 12.4558C2.85938 12.3122 3.0375 12.2404 3.25 12.2404H20.75C20.9625 12.2404 21.1406 12.3123 21.2843 12.4561C21.4281 12.5999 21.5 12.7781 21.5 12.9907C21.5 13.2033 21.4281 13.3814 21.2843 13.525C21.1406 13.6686 20.9625 13.7404 20.75 13.7404H3.25ZM10.9423 10.2596V6.62495H6.5673C6.2735 6.62495 6.02377 6.52201 5.8181 6.31613C5.61245 6.11026 5.50963 5.86027 5.50963 5.56615C5.50963 5.27205 5.61245 5.02083 5.8181 4.8125C6.02377 4.60417 6.2735 4.5 6.5673 4.5H17.4423C17.7361 4.5 17.9858 4.60294 18.1915 4.80883C18.3971 5.01471 18.5 5.2647 18.5 5.5588C18.5 5.85292 18.3971 6.10413 18.1915 6.31245C17.9858 6.52078 17.7361 6.62495 17.4423 6.62495H13.0673V10.2596H10.9423ZM10.9423 15.7211H13.0673V18.4423C13.0673 18.7361 12.9643 18.9858 12.7584 19.1915C12.5526 19.3971 12.3026 19.5 12.0085 19.5C11.7144 19.5 11.4631 19.3962 11.2548 19.1887C11.0465 18.9811 10.9423 18.7291 10.9423 18.4327V15.7211Z"
    />
  </svg>
`;

const tableIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_977_8078)">
      <path
        d="M20 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H20C21.1 21 22 20.1 22 19V5C22 3.9 21.1 3 20 3ZM20 5V8H5V5H20ZM15 19H10V10H15V19ZM5 10H8V19H5V10ZM17 19V10H20V19H17Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_977_8078">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const textIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g clip-path="url(#clip0_992_5547)">
      <path
        d="M5 5.5C5 6.33 5.67 7 6.5 7H10.5V17.5C10.5 18.33 11.17 19 12 19C12.83 19 13.5 18.33 13.5 17.5V7H17.5C18.33 7 19 6.33 19 5.5C19 4.67 18.33 4 17.5 4H6.5C5.67 4 5 4.67 5 5.5Z"
      />
    </g>
    <defs>
      <clipPath id="clip0_992_5547">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
`;

const todoListIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      d="M5.66936 16.3389L9.39244 12.6158C9.54115 12.4671 9.71679 12.3937 9.91936 12.3957C10.1219 12.3976 10.2975 12.4761 10.4463 12.6312C10.5847 12.7823 10.654 12.9585 10.654 13.1599C10.654 13.3613 10.5847 13.5363 10.4463 13.6851L6.32704 17.8197C6.14627 18.0004 5.93538 18.0908 5.69436 18.0908C5.45333 18.0908 5.24243 18.0004 5.06166 17.8197L3.01744 15.7754C2.87899 15.637 2.81136 15.4629 2.81456 15.2533C2.81776 15.0437 2.88859 14.8697 3.02706 14.7312C3.16551 14.5928 3.34008 14.5235 3.55076 14.5235C3.76144 14.5235 3.93494 14.5928 4.07126 14.7312L5.66936 16.3389ZM5.66936 8.72359L9.39244 5.00049C9.54115 4.85177 9.71679 4.77838 9.91936 4.78031C10.1219 4.78223 10.2975 4.86075 10.4463 5.01586C10.5847 5.16691 10.654 5.34314 10.654 5.54454C10.654 5.74592 10.5847 5.92097 10.4463 6.06969L6.32704 10.2043C6.14627 10.3851 5.93538 10.4755 5.69436 10.4755C5.45333 10.4755 5.24243 10.3851 5.06166 10.2043L3.01744 8.16009C2.87899 8.02162 2.81136 7.84759 2.81456 7.63799C2.81776 7.42837 2.88859 7.25433 3.02706 7.11586C3.16551 6.97741 3.34008 6.90819 3.55076 6.90819C3.76144 6.90819 3.93494 6.97741 4.07126 7.11586L5.66936 8.72359ZM13.7597 16.5581C13.5472 16.5581 13.3691 16.4862 13.2253 16.3424C13.0816 16.1986 13.0097 16.0204 13.0097 15.8078C13.0097 15.5952 13.0816 15.4171 13.2253 15.2735C13.3691 15.13 13.5472 15.0582 13.7597 15.0582H20.7597C20.9722 15.0582 21.1503 15.1301 21.2941 15.2739C21.4378 15.4177 21.5097 15.5959 21.5097 15.8085C21.5097 16.0211 21.4378 16.1992 21.2941 16.3427C21.1503 16.4863 20.9722 16.5581 20.7597 16.5581H13.7597ZM13.7597 8.94276C13.5472 8.94276 13.3691 8.87085 13.2253 8.72704C13.0816 8.58324 13.0097 8.40504 13.0097 8.19244C13.0097 7.97985 13.0816 7.80177 13.2253 7.65819C13.3691 7.5146 13.5472 7.44281 13.7597 7.44281H20.7597C20.9722 7.44281 21.1503 7.51471 21.2941 7.65851C21.4378 7.80233 21.5097 7.98053 21.5097 8.19311C21.5097 8.40571 21.4378 8.5838 21.2941 8.72739C21.1503 8.87097 20.9722 8.94276 20.7597 8.94276H13.7597Z"
    />
  </svg>
`;

const translateIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="m12.9 16l-2.55-2.5l.05-.05q1.45-1.6 2.262-3.117T13.95 7H16V5h-6V3H8v2H2v2h9.1q-.5 1.2-1.225 2.387T8 11.7q-.6-.6-1.187-1.412T5.85 8.6H3.85q.5 1.4 1.3 2.7T6.95 13.7L2.85 17.75L4.25 19.15L8 15.4L10.55 17.95zM18.5 22h-2L21 10h2l4.5 12h-2l-1.05-3h-4.9zm1.6-5h3.6l-1.8-5.1z"
    />
  </svg>
`;

const functionsIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M7 19v-.808L13.096 12L7 5.808V5h10v1.25H9.102L14.727 12l-5.625 5.77H17V19z"
    />
  </svg>
`;

const visibilityOffIcon = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height="24px"
    viewBox="0 -960 960 960"
    width="24px"
  >
    <path
      d="m644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302Zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z"
    />
  </svg>
`;

const defaultConfig = {
  [CrepeFeature.CodeMirror]: {
    theme: oneDark,
    languages,
    expandIcon: chevronDownIcon,
    searchIcon,
    clearSearchIcon: clearIcon,
    searchPlaceholder: "Search language",
    noResultText: "No result",
    previewToggleIcon: (previewOnlyMode) => previewOnlyMode ? editIcon : visibilityOffIcon
  }
};

const FeaturesCtx = createSlice([], "FeaturesCtx");
const CrepeCtx = createSlice({}, "CrepeCtx");
function useCrepe(ctx) {
  return ctx.get("CrepeCtx");
}
function useCrepeFeatures(ctx) {
  return ctx.use("FeaturesCtx");
}
function crepeFeatureConfig(feature) {
  return (ctx) => {
    useCrepeFeatures(ctx).update((features) => {
      if (features.includes(feature)) {
        return features;
      }
      return [...features, feature];
    });
  };
}

function defaultBuildContext(ctx, instruction) {
  var _a;
  const view = ctx.get(editorViewCtx);
  const serializer = ctx.get(serializerCtx);
  const { state } = view;
  const document = serializer(state.doc);
  let selection = "";
  if (!state.selection.empty) {
    const { from, to } = state.selection;
    const slice = state.doc.slice(from, to);
    const { schema } = state.doc.type;
    let wrapper = schema.topNodeType.createAndFill(null, slice.content);
    if (!wrapper) {
      const paragraph = (_a = schema.nodes.paragraph) == null ? void 0 : _a.createAndFill(
        null,
        slice.content
      );
      if (paragraph) wrapper = schema.topNodeType.createAndFill(null, paragraph);
    }
    selection = wrapper ? serializer(wrapper) : state.doc.textBetween(from, to);
  }
  return { document, selection, instruction };
}

const aiProviderConfig = $ctx(
  {
    provider: void 0,
    buildContext: void 0,
    diffReviewOnEnd: true,
    onError: (error) => {
      console.error(`[milkdown/ai] [${error.code}]`, error);
    },
    aiIcon: void 0
  },
  "aiProviderConfig"
);
const aiSessionCtx = $ctx(
  {
    abortController: null,
    label: "",
    lastInstruction: "",
    lastLabel: void 0,
    lastFrom: -1,
    lastTo: -1,
    diffOwnedByAI: false
  },
  "aiSession"
);
function emitAIError(ctx, error) {
  const config = ctx.get(aiProviderConfig.key);
  try {
    config.onError(error);
  } catch (handlerError) {
    console.error("[milkdown/ai] onError handler failed:", handlerError);
  }
}
function clearActiveSession(ctx) {
  const current = ctx.get(aiSessionCtx.key);
  ctx.set(aiSessionCtx.key, {
    ...current,
    abortController: null,
    label: ""
  });
}
async function runProvider(ctx, provider, promptContext, abortController) {
  try {
    const iterable = provider(promptContext, abortController.signal);
    const commands = ctx.get(commandsCtx);
    for await (const chunk of iterable) {
      if (abortController.signal.aborted) break;
      commands.call(pushChunkCmd.key, chunk);
    }
    if (abortController.signal.aborted) return;
    const config = ctx.get(aiProviderConfig.key);
    if (config.diffReviewOnEnd) {
      const cur = ctx.get(aiSessionCtx.key);
      ctx.set(aiSessionCtx.key, { ...cur, diffOwnedByAI: true });
    }
    const dispatched = commands.call(endStreamingCmd.key, {
      diffReview: config.diffReviewOnEnd
    });
    if (config.diffReviewOnEnd && !dispatched) {
      const cur = ctx.get(aiSessionCtx.key);
      ctx.set(aiSessionCtx.key, { ...cur, diffOwnedByAI: false });
    }
  } catch (error) {
    if (abortController.signal.aborted) return;
    const milkdownError = aiProviderError(error);
    emitAIError(ctx, milkdownError);
    const commands = ctx.get(commandsCtx);
    commands.call(abortStreamingCmd.key, { keep: false });
  } finally {
    const current = ctx.get(aiSessionCtx.key);
    if (current.abortController === abortController) {
      clearActiveSession(ctx);
    }
  }
}
const runAICmd = $command("RunAI", (ctx) => {
  return (options) => (state, dispatch) => {
    var _a, _b, _c, _d;
    if (!(options == null ? void 0 : options.instruction)) return false;
    const config = ctx.get(aiProviderConfig.key);
    if (!config.provider) return false;
    const session = ctx.get(aiSessionCtx.key);
    if (session.abortController) return false;
    if ((_a = streamingPluginKey.getState(state)) == null ? void 0 : _a.active) return false;
    if ((_b = diffPluginKey.getState(state)) == null ? void 0 : _b.active) return false;
    if (!dispatch) return true;
    const abortController = new AbortController();
    const { from, to } = state.selection;
    ctx.set(aiSessionCtx.key, {
      abortController,
      label: (_c = options.label) != null ? _c : "",
      lastInstruction: options.instruction,
      lastLabel: options.label,
      lastFrom: from,
      lastTo: to,
      // Reset every run; only the success path that hands off to diff
      // review flips it back on.
      diffOwnedByAI: false
    });
    const commands = ctx.get(commandsCtx);
    const insertAt = state.selection.empty ? "cursor" : "selection";
    if (!commands.call(startStreamingCmd.key, { insertAt })) {
      clearActiveSession(ctx);
      return false;
    }
    let promptContext;
    try {
      const buildContext = (_d = config.buildContext) != null ? _d : defaultBuildContext;
      promptContext = buildContext(ctx, options.instruction);
    } catch (error) {
      const milkdownError = aiBuildContextError(error);
      emitAIError(ctx, milkdownError);
      commands.call(abortStreamingCmd.key, { keep: false });
      clearActiveSession(ctx);
      return false;
    }
    void runProvider(ctx, config.provider, promptContext, abortController);
    return true;
  };
});
const abortAICmd = $command("AbortAI", (ctx) => {
  return (options) => (state, dispatch) => {
    var _a;
    const session = ctx.get(aiSessionCtx.key);
    if (!dispatch) return !!session.abortController;
    if (!session.abortController) return false;
    session.abortController.abort();
    clearActiveSession(ctx);
    if ((_a = streamingPluginKey.getState(state)) == null ? void 0 : _a.active) {
      const commands = ctx.get(commandsCtx);
      commands.call(abortStreamingCmd.key, options);
    }
    return true;
  };
});

var __typeError$a = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$a = (obj, member, msg) => member.has(obj) || __typeError$a("Cannot " + msg);
var __privateGet$a = (obj, member, getter) => (__accessCheck$a(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$a = (obj, member, value) => member.has(obj) ? __typeError$a("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$a = (obj, member, value, setter) => (__accessCheck$a(obj, member, "write to private field"), member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck$a(obj, member, "access private method"), method);
var _panel, _host, _retryBtn, _config, _visible, _diffActive, _diffStartDoc, _ownedByAI, _DiffActionsPanelView_instances, findHost_fn, makeButton_fn, makeShortcutChip_fn, _retry, canRetry_fn, _rejectAll, _acceptAll;
const PANEL_CLASS = "milkdown-ai-diff-actions";
function setSanitizedIcon(host, svg) {
  host.innerHTML = DOMPurify.sanitize(svg.trim());
}
function createIcon(svg) {
  const span = document.createElement("span");
  span.className = `${PANEL_CLASS}-icon`;
  setSanitizedIcon(span, svg);
  return span;
}
class DiffActionsPanelView {
  constructor(ctx, view, config) {
    this.ctx = ctx;
    __privateAdd$a(this, _DiffActionsPanelView_instances);
    __privateAdd$a(this, _panel);
    __privateAdd$a(this, _host);
    __privateAdd$a(this, _retryBtn);
    __privateAdd$a(this, _config);
    __privateAdd$a(this, _visible, false);
    /// Tracks the diff plugin's `active` flag across transactions so we
    /// can detect false→true / true→false edges independently of whether
    /// the panel is actually being shown.
    __privateAdd$a(this, _diffActive, false);
    /// Doc snapshot at the moment diff review activated. If the live doc
    /// drifts from this snapshot the user has accepted some per-change
    /// diffs and the stored `lastFrom`/`lastTo` no longer point at the
    /// original range — Retry is unsafe at that point.
    __privateAdd$a(this, _diffStartDoc, null);
    /// Whether the active diff review came from this AI session's
    /// streaming hand-off (vs being started manually via
    /// `startDiffReviewCmd`). Captured at the false→true transition.
    /// The panel only renders when this is true so it doesn't take over
    /// non-AI diff flows that exist independently of the AI feature.
    __privateAdd$a(this, _ownedByAI, false);
    __privateAdd$a(this, _retry, () => {
      const session = this.ctx.get(aiSessionCtx.key);
      if (!session.lastInstruction) return;
      if (!__privateGet$a(this, _ownedByAI) || !__privateMethod(this, _DiffActionsPanelView_instances, canRetry_fn).call(this)) return;
      const commands = this.ctx.get(commandsCtx);
      commands.call(clearDiffReviewCmd.key);
      const editorView = this.ctx.get(editorViewCtx);
      const { doc } = editorView.state;
      const from = Math.min(Math.max(session.lastFrom, 0), doc.content.size);
      const to = Math.min(Math.max(session.lastTo, 0), doc.content.size);
      editorView.dispatch(
        editorView.state.tr.setSelection(
          TextSelection.create(editorView.state.doc, from, to)
        )
      );
      commands.call(runAICmd.key, {
        instruction: session.lastInstruction,
        label: session.lastLabel
      });
    });
    __privateAdd$a(this, _rejectAll, () => {
      this.ctx.get(commandsCtx).call(clearDiffReviewCmd.key);
    });
    __privateAdd$a(this, _acceptAll, () => {
      this.ctx.get(commandsCtx).call(acceptAllDiffsCmd.key);
    });
    __privateSet$a(this, _config, config);
    __privateSet$a(this, _host, __privateMethod(this, _DiffActionsPanelView_instances, findHost_fn).call(this, view));
    const panel = document.createElement("div");
    panel.className = PANEL_CLASS;
    panel.dataset.show = "false";
    __privateSet$a(this, _retryBtn, __privateMethod(this, _DiffActionsPanelView_instances, makeButton_fn).call(this, "retry", config.retryIcon, config.retryLabel, __privateGet$a(this, _retry)));
    panel.appendChild(__privateGet$a(this, _retryBtn));
    panel.appendChild(
      __privateMethod(this, _DiffActionsPanelView_instances, makeButton_fn).call(this, "reject", config.rejectIcon, config.rejectAllLabel, __privateGet$a(this, _rejectAll))
    );
    const acceptBtn = __privateMethod(this, _DiffActionsPanelView_instances, makeButton_fn).call(this, "accept", config.acceptIcon, config.acceptAllLabel, __privateGet$a(this, _acceptAll));
    acceptBtn.appendChild(__privateMethod(this, _DiffActionsPanelView_instances, makeShortcutChip_fn).call(this));
    panel.appendChild(acceptBtn);
    __privateSet$a(this, _panel, panel);
    __privateGet$a(this, _host).appendChild(panel);
    this.update(view);
  }
  update(view) {
    var _a;
    const diffActive = !!((_a = diffPluginKey.getState(view.state)) == null ? void 0 : _a.active);
    if (diffActive !== __privateGet$a(this, _diffActive)) {
      __privateSet$a(this, _diffActive, diffActive);
      if (diffActive) {
        const session = this.ctx.get(aiSessionCtx.key);
        __privateSet$a(this, _ownedByAI, session.diffOwnedByAI);
        __privateSet$a(this, _diffStartDoc, view.state.doc);
      } else {
        __privateSet$a(this, _ownedByAI, false);
        __privateSet$a(this, _diffStartDoc, null);
        const session = this.ctx.get(aiSessionCtx.key);
        if (session.diffOwnedByAI) {
          this.ctx.set(aiSessionCtx.key, {
            ...session,
            diffOwnedByAI: false
          });
        }
      }
    }
    const shouldShow = diffActive && __privateGet$a(this, _ownedByAI);
    if (shouldShow !== __privateGet$a(this, _visible)) {
      __privateSet$a(this, _visible, shouldShow);
      __privateGet$a(this, _panel).dataset.show = shouldShow ? "true" : "false";
    }
    if (shouldShow) {
      const session = this.ctx.get(aiSessionCtx.key);
      const docUntouched = !!__privateGet$a(this, _diffStartDoc) && view.state.doc.eq(__privateGet$a(this, _diffStartDoc));
      __privateGet$a(this, _retryBtn).disabled = !session.lastInstruction || !docUntouched;
    }
  }
  destroy() {
    __privateGet$a(this, _panel).remove();
  }
}
_panel = new WeakMap();
_host = new WeakMap();
_retryBtn = new WeakMap();
_config = new WeakMap();
_visible = new WeakMap();
_diffActive = new WeakMap();
_diffStartDoc = new WeakMap();
_ownedByAI = new WeakMap();
_DiffActionsPanelView_instances = new WeakSet();
findHost_fn = function(view) {
  var _a;
  return (_a = view.dom.closest(".milkdown")) != null ? _a : document.body;
};
makeButton_fn = function(variant, icon, label, onClick) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `${PANEL_CLASS}-btn ${PANEL_CLASS}-btn-${variant}`;
  btn.appendChild(createIcon(icon));
  const text = document.createElement("span");
  text.textContent = label;
  btn.appendChild(text);
  btn.addEventListener("mousedown", (e) => e.preventDefault());
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return btn;
};
makeShortcutChip_fn = function() {
  const shortcut = document.createElement("span");
  shortcut.className = `${PANEL_CLASS}-shortcut`;
  const cmd = document.createElement("span");
  cmd.textContent = __privateGet$a(this, _config).modSymbol;
  const enter = document.createElement("span");
  enter.className = `${PANEL_CLASS}-shortcut-icon`;
  setSanitizedIcon(enter, __privateGet$a(this, _config).enterKeyIcon);
  shortcut.append(cmd, enter);
  return shortcut;
};
_retry = new WeakMap();
canRetry_fn = function() {
  if (!__privateGet$a(this, _diffStartDoc)) return false;
  const editorView = this.ctx.get(editorViewCtx);
  return editorView.state.doc.eq(__privateGet$a(this, _diffStartDoc));
};
_rejectAll = new WeakMap();
_acceptAll = new WeakMap();

const diffActionsPanelKey = new PluginKey("CREPE_AI_DIFF_ACTIONS_PANEL");
const DEFAULT_DIFF_ACTIONS_RETRY_LABEL = "Retry";
const DEFAULT_DIFF_ACTIONS_REJECT_ALL_LABEL = "Reject all";
const DEFAULT_DIFF_ACTIONS_ACCEPT_ALL_LABEL = "Accept all";
function detectModSymbol() {
  var _a, _b, _c, _d;
  if (typeof navigator === "undefined") return "\u2318";
  const ua = navigator;
  const platform = (_d = (_c = (_b = (_a = ua.userAgentData) == null ? void 0 : _a.platform) != null ? _b : ua.platform) != null ? _c : ua.userAgent) != null ? _d : "";
  return /mac|iphone|ipad|ipod/i.test(platform) ? "\u2318" : "Ctrl";
}
const DEFAULT_DIFF_ACTIONS_MOD_SYMBOL = detectModSymbol();
function resolveDiffActionsConfig(options) {
  var _a, _b, _c, _d, _e, _f, _g;
  const { config, enterKeyIcon: enterKeyIcon$1 } = options;
  return {
    retryLabel: (_a = config == null ? void 0 : config.retryLabel) != null ? _a : DEFAULT_DIFF_ACTIONS_RETRY_LABEL,
    rejectAllLabel: (_b = config == null ? void 0 : config.rejectAllLabel) != null ? _b : DEFAULT_DIFF_ACTIONS_REJECT_ALL_LABEL,
    acceptAllLabel: (_c = config == null ? void 0 : config.acceptAllLabel) != null ? _c : DEFAULT_DIFF_ACTIONS_ACCEPT_ALL_LABEL,
    retryIcon: (_d = config == null ? void 0 : config.retryIcon) != null ? _d : retryIcon,
    rejectIcon: (_e = config == null ? void 0 : config.rejectIcon) != null ? _e : clearIcon,
    acceptIcon: (_f = config == null ? void 0 : config.acceptIcon) != null ? _f : confirmIcon,
    enterKeyIcon: enterKeyIcon$1 != null ? enterKeyIcon$1 : enterKeyIcon,
    modSymbol: (_g = config == null ? void 0 : config.modSymbol) != null ? _g : DEFAULT_DIFF_ACTIONS_MOD_SYMBOL
  };
}
function diffActionsPanelPlugin(options = {}) {
  const resolved = resolveDiffActionsConfig(options);
  return $prose((ctx) => {
    return new Plugin({
      key: diffActionsPanelKey,
      view(view) {
        return new DiffActionsPanelView(ctx, view, resolved);
      },
      props: {
        handleKeyDown(view, event) {
          var _a;
          if (event.key !== "Enter") return false;
          if (!(event.metaKey || event.ctrlKey)) return false;
          if (!((_a = diffPluginKey.getState(view.state)) == null ? void 0 : _a.active)) return false;
          if (!ctx.get(aiSessionCtx.key).diffOwnedByAI) return false;
          event.preventDefault();
          const commands = ctx.get(commandsCtx);
          commands.call(acceptAllDiffsCmd.key);
          return true;
        }
      }
    });
  });
}

var __typeError$9 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$9 = (obj, member, msg) => member.has(obj) || __typeError$9("Cannot " + msg);
var __privateGet$9 = (obj, member, getter) => (__accessCheck$9(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$9 = (obj, member, value) => member.has(obj) ? __typeError$9("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$9 = (obj, member, value, setter) => (__accessCheck$9(obj, member, "write to private field"), member.set(obj, value), value);
var _nodes, _removeById;
function createSubmenuBuilder(node) {
  const builder = {
    addItem: (id, item) => {
      node.items.set(id, item);
      return builder;
    },
    removeItem: (id) => {
      node.items.delete(id);
      return builder;
    },
    getItem: (id) => node.items.get(id),
    clear: () => {
      node.items.clear();
      return builder;
    }
  };
  return builder;
}
class AISuggestionsBuilder {
  constructor() {
    __privateAdd$9(this, _nodes, []);
    this.addItem = (id, item) => {
      __privateGet$9(this, _removeById).call(this, id);
      __privateGet$9(this, _nodes).push({ kind: "item", id, item });
      return this;
    };
    /// Add a submenu. Populate items via the optional `build` callback,
    /// or call `getSubmenu(id)` afterward. Returns `this` so calls can be
    /// chained at the parent level alongside `addItem`.
    this.addSubmenu = (id, def, build) => {
      __privateGet$9(this, _removeById).call(this, id);
      const node = { def, items: /* @__PURE__ */ new Map() };
      if (build) build(createSubmenuBuilder(node));
      __privateGet$9(this, _nodes).push({ kind: "submenu", id, node });
      return this;
    };
    this.removeItem = (id) => {
      __privateGet$9(this, _removeById).call(this, id);
      return this;
    };
    this.getItem = (id) => {
      const node = __privateGet$9(this, _nodes).find((n) => n.kind === "item" && n.id === id);
      return (node == null ? void 0 : node.kind) === "item" ? node.item : void 0;
    };
    /// Return a builder that mutates the submenu's items in place.
    /// Multiple calls return distinct builder objects backed by the same
    /// underlying node, so changes are always visible.
    this.getSubmenu = (id) => {
      const node = __privateGet$9(this, _nodes).find((n) => n.kind === "submenu" && n.id === id);
      return (node == null ? void 0 : node.kind) === "submenu" ? createSubmenuBuilder(node.node) : void 0;
    };
    this.clear = () => {
      __privateSet$9(this, _nodes, []);
      return this;
    };
    this.build = () => {
      const main = [];
      const submenus = {};
      for (const node of __privateGet$9(this, _nodes)) {
        if (node.kind === "item") {
          main.push({ kind: "item", id: node.id, item: node.item });
        } else {
          main.push({ kind: "submenu", id: node.id, def: node.node.def });
          submenus[node.id] = {
            def: node.node.def,
            items: Array.from(node.node.items.entries()).map(([id, item]) => ({
              id,
              item
            }))
          };
        }
      }
      return { main, submenus };
    };
    __privateAdd$9(this, _removeById, (id) => {
      __privateSet$9(this, _nodes, __privateGet$9(this, _nodes).filter((n) => n.id !== id));
    });
  }
}
_nodes = new WeakMap();
_removeById = new WeakMap();
function applyDefaultSuggestions(builder) {
  builder.addItem("improve", {
    icon: aiIcon,
    label: "Improve writing",
    streamingLabel: "Improving writing",
    prompt: "Improve the writing while preserving the original meaning."
  }).addItem("grammar", {
    icon: grammarCheckIcon,
    label: "Fix grammar & spelling",
    streamingLabel: "Fixing grammar & spelling",
    prompt: "Fix any grammar and spelling errors without changing the meaning."
  }).addItem("shorter", {
    icon: shorterIcon,
    label: "Make shorter",
    streamingLabel: "Making shorter",
    prompt: "Make this shorter while preserving the key information."
  }).addItem("longer", {
    icon: longerIcon,
    label: "Make longer",
    streamingLabel: "Expanding",
    prompt: "Expand this with more detail and examples."
  });
  builder.addSubmenu(
    "tone",
    {
      icon: editIcon,
      label: "Change tone\u2026",
      title: "Change tone",
      searchPlaceholder: "Search tones\u2026"
    },
    (sub) => {
      const tones = [
        ["professional", "Professional"],
        ["casual", "Casual"],
        ["confident", "Confident"],
        ["friendly", "Friendly"],
        ["direct", "Direct"],
        ["formal", "Formal"]
      ];
      for (const [id, label] of tones) {
        sub.addItem(id, {
          icon: editIcon,
          label,
          streamingLabel: "Adjusting tone",
          prompt: `Rewrite this in a ${label.toLowerCase()} tone.`
        });
      }
    }
  );
  builder.addSubmenu(
    "translate",
    {
      icon: translateIcon,
      label: "Translate\u2026",
      title: "Translate",
      searchPlaceholder: "Search languages\u2026"
    },
    (sub) => {
      const languages = [
        ["english", "English", "English"],
        ["chinese", "Chinese", "Chinese (Simplified)"],
        ["japanese", "Japanese", "Japanese"],
        ["korean", "Korean", "Korean"],
        ["spanish", "Spanish", "Spanish"],
        ["french", "French", "French"],
        ["german", "German", "German"]
      ];
      for (const [id, label, promptName] of languages) {
        sub.addItem(id, {
          icon: translateIcon,
          label,
          streamingLabel: `Translating to ${label}`,
          prompt: `Translate this to ${promptName}.`
        });
      }
    }
  );
}

function keepAlive(..._args) {
}

keepAlive(h);
function renderHighlighted(label, query) {
  const q = query.trim();
  if (!q) return [label];
  const lower = label.toLowerCase();
  const lq = q.toLowerCase();
  const idx = lower.indexOf(lq);
  if (idx === -1) return [label];
  return [
    label.slice(0, idx),
    h("mark", null, label.slice(idx, idx + q.length)),
    label.slice(idx + q.length)
  ];
}
const AIInstructionInput = defineComponent({
  props: {
    placeholder: { type: Object, required: true },
    resetSignal: { type: Object, required: true },
    suggestions: { type: Object, required: true },
    chrome: { type: Object, required: true },
    onConfirm: { type: Function, required: true },
    onCancel: { type: Function, required: true }
  },
  setup({
    placeholder,
    resetSignal,
    suggestions,
    chrome,
    onConfirm,
    onCancel
  }) {
    const inputValue = ref("");
    const view = ref({ kind: "main" });
    const selectedIndex = ref(0);
    const inputRef = ref(null);
    const listRef = ref(null);
    const listboxId = `ai-instruction-list-${Math.random().toString(36).slice(2, 9)}`;
    const optionId = (idx) => `${listboxId}-opt-${idx}`;
    watch(resetSignal, () => {
      inputValue.value = "";
      view.value = { kind: "main" };
      selectedIndex.value = 0;
    });
    const allItems = computed(() => {
      if (view.value.kind === "submenu") {
        const submenu = suggestions.submenus[view.value.id];
        if (!submenu) return [];
        return submenu.items.map(({ id, item }) => ({
          id,
          icon: item.icon,
          label: item.label,
          hasSubmenu: false,
          prompt: { text: item.prompt, streamingLabel: item.streamingLabel }
        }));
      }
      return suggestions.main.map((entry) => {
        if (entry.kind === "item") {
          return {
            id: entry.id,
            icon: entry.item.icon,
            label: entry.item.label,
            hasSubmenu: false,
            prompt: {
              text: entry.item.prompt,
              streamingLabel: entry.item.streamingLabel
            }
          };
        }
        return {
          id: entry.id,
          icon: entry.def.icon,
          label: entry.def.label,
          hasSubmenu: true
        };
      });
    });
    const currentSubmenuDef = computed(() => {
      var _a, _b;
      if (view.value.kind !== "submenu") return null;
      return (_b = (_a = suggestions.submenus[view.value.id]) == null ? void 0 : _a.def) != null ? _b : null;
    });
    const filteredItems = computed(() => {
      const q = inputValue.value.trim().toLowerCase();
      if (!q) return allItems.value;
      return allItems.value.filter(
        (item) => item.label.toLowerCase().includes(q)
      );
    });
    const showSendAsPrompt = computed(() => inputValue.value.trim().length > 0);
    const totalItems = computed(
      () => filteredItems.value.length + (showSendAsPrompt.value ? 1 : 0)
    );
    watch([filteredItems, view, showSendAsPrompt], () => {
      selectedIndex.value = showSendAsPrompt.value ? filteredItems.value.length : 0;
    });
    const focusInput = () => {
      void nextTick(() => {
        var _a;
        return (_a = inputRef.value) == null ? void 0 : _a.focus();
      });
    };
    const enterSubmenu = (id) => {
      view.value = { kind: "submenu", id };
      inputValue.value = "";
      selectedIndex.value = 0;
      focusInput();
    };
    const exitSubmenu = () => {
      view.value = { kind: "main" };
      inputValue.value = "";
      selectedIndex.value = 0;
      focusInput();
    };
    const runItem = (item) => {
      if (item.hasSubmenu) {
        enterSubmenu(item.id);
      } else if (item.prompt) {
        onConfirm(item.prompt.text, item.prompt.streamingLabel);
        inputValue.value = "";
      }
    };
    const submitRaw = () => {
      const v = inputValue.value.trim();
      if (!v) return;
      onConfirm(v);
      inputValue.value = "";
    };
    const onSelectCurrent = () => {
      const idx = selectedIndex.value;
      const items = filteredItems.value;
      if (idx < items.length) {
        runItem(items[idx]);
      } else if (showSendAsPrompt.value) {
        submitRaw();
      }
    };
    const scrollToSelected = () => {
      void nextTick(() => {
        const list = listRef.value;
        if (!list) return;
        const el = list.querySelector(
          `[data-index="${selectedIndex.value}"]`
        );
        el == null ? void 0 : el.scrollIntoView({ block: "nearest" });
      });
    };
    const onKeydown = (e) => {
      e.stopPropagation();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (totalItems.value === 0) return;
        selectedIndex.value = (selectedIndex.value + 1) % totalItems.value;
        scrollToSelected();
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (totalItems.value === 0) return;
        selectedIndex.value = (selectedIndex.value - 1 + totalItems.value) % totalItems.value;
        scrollToSelected();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        onSelectCurrent();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (view.value.kind === "submenu") exitSubmenu();
        else onCancel();
        return;
      }
      if (e.key === "Backspace" && inputValue.value === "" && view.value.kind === "submenu") {
        e.preventDefault();
        exitSubmenu();
      }
    };
    const onItemPointerDown = (e) => {
      e.preventDefault();
    };
    return () => {
      const items = filteredItems.value;
      const showPrompt = showSendAsPrompt.value;
      const submenuDef = currentSubmenuDef.value;
      return /* @__PURE__ */ h("div", { class: "ai-instruction" }, /* @__PURE__ */ h("div", { class: "ai-instruction-input" }, /* @__PURE__ */ h("span", { class: "ai-instruction-input-prefix" }, /* @__PURE__ */ h(Icon, { icon: chrome.aiIcon })), /* @__PURE__ */ h(
        "input",
        {
          ref: inputRef,
          class: "ai-instruction-input-field",
          role: "combobox",
          "aria-expanded": "true",
          "aria-autocomplete": "list",
          "aria-controls": listboxId,
          "aria-activedescendant": totalItems.value > 0 ? optionId(selectedIndex.value) : void 0,
          placeholder: submenuDef ? submenuDef.searchPlaceholder : placeholder.value,
          value: inputValue.value,
          onInput: (e) => {
            inputValue.value = e.target.value;
          },
          onKeydown
        }
      ), /* @__PURE__ */ h(
        "button",
        {
          type: "button",
          class: "ai-instruction-submit",
          "aria-label": chrome.submitButtonLabel,
          disabled: !showPrompt,
          onMousedown: onItemPointerDown,
          onClick: submitRaw
        },
        /* @__PURE__ */ h(Icon, { icon: chrome.sendIcon })
      )), /* @__PURE__ */ h(
        "div",
        {
          class: "ai-instruction-list",
          ref: listRef,
          id: listboxId,
          role: "listbox",
          "aria-label": chrome.listboxLabel
        },
        submenuDef && /* @__PURE__ */ h(
          "button",
          {
            type: "button",
            class: "ai-instruction-back",
            onMousedown: onItemPointerDown,
            onClick: exitSubmenu
          },
          /* @__PURE__ */ h("span", { class: "ai-instruction-back-icon", "aria-hidden": "true" }, /* @__PURE__ */ h(Icon, { icon: chrome.chevronLeftIcon })),
          /* @__PURE__ */ h("span", null, submenuDef.title)
        ),
        items.length > 0 && /* @__PURE__ */ h("div", { class: "ai-instruction-section" }, /* @__PURE__ */ h("div", { class: "ai-instruction-section-header" }, chrome.suggestionsHeaderLabel), items.map((item, idx) => /* @__PURE__ */ h(
          "div",
          {
            key: item.id,
            id: optionId(idx),
            "data-index": idx,
            role: "option",
            "aria-selected": idx === selectedIndex.value,
            class: [
              "ai-instruction-item",
              idx === selectedIndex.value ? "active" : ""
            ],
            onMousedown: onItemPointerDown,
            onClick: () => runItem(item),
            onPointerenter: () => {
              selectedIndex.value = idx;
            }
          },
          /* @__PURE__ */ h("span", { class: "ai-instruction-item-icon" }, /* @__PURE__ */ h(Icon, { icon: item.icon })),
          /* @__PURE__ */ h("span", { class: "ai-instruction-item-label" }, renderHighlighted(item.label, inputValue.value)),
          item.hasSubmenu && /* @__PURE__ */ h("span", { class: "ai-instruction-item-arrow" }, /* @__PURE__ */ h(Icon, { icon: chrome.chevronRightIcon }))
        ))),
        showPrompt && /* @__PURE__ */ h("div", { class: "ai-instruction-section" }, /* @__PURE__ */ h("div", { class: "ai-instruction-section-header" }, chrome.sendAsPromptHeaderLabel), /* @__PURE__ */ h(
          "div",
          {
            id: optionId(items.length),
            "data-index": items.length,
            role: "option",
            "aria-selected": selectedIndex.value === items.length,
            class: [
              "ai-instruction-item",
              "ai-instruction-item-prompt",
              selectedIndex.value === items.length ? "active" : ""
            ],
            onMousedown: onItemPointerDown,
            onClick: submitRaw,
            onPointerenter: () => {
              selectedIndex.value = items.length;
            }
          },
          /* @__PURE__ */ h("span", { class: "ai-instruction-item-icon" }, /* @__PURE__ */ h(Icon, { icon: chrome.sendPromptIcon })),
          /* @__PURE__ */ h("span", { class: "ai-instruction-item-label" }, chrome.sendAsPromptLabel, " ", /* @__PURE__ */ h("span", { class: "ai-instruction-item-quote" }, '"', inputValue.value, '"')),
          /* @__PURE__ */ h("span", { class: "ai-instruction-item-shortcut" }, /* @__PURE__ */ h(Icon, { icon: chrome.enterKeyIcon }))
        ))
      ));
    };
  }
});

var __typeError$8 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$8 = (obj, member, msg) => member.has(obj) || __typeError$8("Cannot " + msg);
var __privateGet$8 = (obj, member, getter) => (__accessCheck$8(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$8 = (obj, member, value) => member.has(obj) ? __typeError$8("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$8 = (obj, member, value, setter) => (__accessCheck$8(obj, member, "write to private field"), member.set(obj, value), value);
var _content$4, _provider$2, _app$5, _placeholder, _resetSignal, _from, _to, _wantsShow, _onConfirm, _onCancel;
class AIInstructionTooltipView {
  constructor(ctx, view, config) {
    this.ctx = ctx;
    __privateAdd$8(this, _content$4);
    __privateAdd$8(this, _provider$2);
    __privateAdd$8(this, _app$5);
    __privateAdd$8(this, _placeholder);
    __privateAdd$8(this, _resetSignal);
    __privateAdd$8(this, _from, -1);
    __privateAdd$8(this, _to, -1);
    /// Source of truth for "should the palette currently be showing".
    /// `TooltipProvider.shouldShow` reads this so that forwarding `update()`
    /// to the provider on every editor transition (needed to keep the
    /// floating position in sync with layout changes) doesn't dismiss the
    /// palette behind our back.
    __privateAdd$8(this, _wantsShow, false);
    __privateAdd$8(this, _onConfirm, (instruction, label) => {
      if (!instruction.trim()) return;
      const commands = this.ctx.get(commandsCtx);
      const accepted = commands.call(runAICmd.key, { instruction, label });
      if (!accepted) return;
      __privateSet$8(this, _wantsShow, false);
      __privateGet$8(this, _provider$2).hide();
    });
    __privateAdd$8(this, _onCancel, () => {
      __privateSet$8(this, _wantsShow, false);
      __privateGet$8(this, _provider$2).hide();
    });
    this.show = (from, to) => {
      __privateSet$8(this, _from, from);
      __privateSet$8(this, _to, to);
      __privateGet$8(this, _resetSignal).value++;
      __privateSet$8(this, _wantsShow, true);
      const view = this.ctx.get(editorViewCtx);
      __privateGet$8(this, _provider$2).show(
        { getBoundingClientRect: () => posToDOMRect(view, from, to) },
        view
      );
      requestAnimationFrame(() => {
        var _a;
        (_a = __privateGet$8(this, _content$4).querySelector("input")) == null ? void 0 : _a.focus();
      });
    };
    this.update = (view, prevState) => {
      const { selection } = view.state;
      const isTextSelection = selection instanceof TextSelection;
      const movedRange = selection.from !== __privateGet$8(this, _from) || selection.to !== __privateGet$8(this, _to);
      if (!isTextSelection || movedRange) {
        __privateSet$8(this, _wantsShow, false);
        __privateGet$8(this, _provider$2).hide();
        return;
      }
      __privateGet$8(this, _provider$2).update(view, prevState);
    };
    this.destroy = () => {
      __privateGet$8(this, _app$5).unmount();
      __privateGet$8(this, _provider$2).destroy();
      __privateGet$8(this, _content$4).remove();
    };
    __privateSet$8(this, _placeholder, ref(config.placeholder));
    __privateSet$8(this, _resetSignal, ref(0));
    const content = document.createElement("div");
    content.className = "milkdown-ai-instruction";
    const app = createApp(AIInstructionInput, {
      placeholder: __privateGet$8(this, _placeholder),
      resetSignal: __privateGet$8(this, _resetSignal),
      suggestions: config.suggestions,
      chrome: config.chrome,
      onConfirm: __privateGet$8(this, _onConfirm),
      onCancel: __privateGet$8(this, _onCancel)
    });
    app.mount(content);
    __privateSet$8(this, _app$5, app);
    __privateSet$8(this, _content$4, content);
    __privateSet$8(this, _provider$2, new TooltipProvider({
      content,
      debounce: 0,
      offset: 10,
      shouldShow: () => __privateGet$8(this, _wantsShow),
      floatingUIOptions: {
        placement: "bottom"
      }
    }));
    __privateGet$8(this, _provider$2).onHide = () => {
      __privateSet$8(this, _wantsShow, false);
      requestAnimationFrame(() => {
        try {
          const root = __privateGet$8(this, _content$4).getRootNode();
          const active = root.activeElement;
          if (!active || !__privateGet$8(this, _content$4).contains(active)) return;
          const v = this.ctx.get(editorViewCtx);
          v.dom.focus({ preventScroll: true });
        } catch (e) {
        }
      });
    };
    __privateGet$8(this, _provider$2).update(view);
  }
}
_content$4 = new WeakMap();
_provider$2 = new WeakMap();
_app$5 = new WeakMap();
_placeholder = new WeakMap();
_resetSignal = new WeakMap();
_from = new WeakMap();
_to = new WeakMap();
_wantsShow = new WeakMap();
_onConfirm = new WeakMap();
_onCancel = new WeakMap();

const defaultAPI = {
  show: () => {
  }
};
const aiInstructionTooltipAPI = $ctx(
  { ...defaultAPI },
  "aiInstructionTooltipAPI"
);
const aiInstructionTooltip = tooltipFactory("CREPE_AI_INSTRUCTION");
const DEFAULT_SUGGESTIONS_HEADER_LABEL = "SUGGESTIONS";
const DEFAULT_SEND_AS_PROMPT_HEADER_LABEL = "SEND AS PROMPT";
const DEFAULT_SEND_AS_PROMPT_LABEL = "Ask AI:";
const DEFAULT_SUBMIT_BUTTON_LABEL = "Send prompt";
const DEFAULT_LISTBOX_LABEL = "AI suggestions";
const DEFAULT_INSTRUCTION_PLACEHOLDER = "Tell AI what to do with the selection\u2026";
function resolveChrome(config) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  return {
    aiIcon: (_a = config == null ? void 0 : config.aiIcon) != null ? _a : aiIcon,
    sendIcon: (_b = config == null ? void 0 : config.sendIcon) != null ? _b : sendIcon,
    sendPromptIcon: (_c = config == null ? void 0 : config.sendPromptIcon) != null ? _c : sendPromptIcon,
    enterKeyIcon: (_d = config == null ? void 0 : config.enterKeyIcon) != null ? _d : enterKeyIcon,
    chevronLeftIcon: (_e = config == null ? void 0 : config.chevronLeftIcon) != null ? _e : chevronLeftIcon,
    chevronRightIcon: (_f = config == null ? void 0 : config.chevronRightIcon) != null ? _f : chevronRightIcon,
    suggestionsHeaderLabel: (_g = config == null ? void 0 : config.suggestionsHeaderLabel) != null ? _g : DEFAULT_SUGGESTIONS_HEADER_LABEL,
    sendAsPromptHeaderLabel: (_h = config == null ? void 0 : config.sendAsPromptHeaderLabel) != null ? _h : DEFAULT_SEND_AS_PROMPT_HEADER_LABEL,
    sendAsPromptLabel: (_i = config == null ? void 0 : config.sendAsPromptLabel) != null ? _i : DEFAULT_SEND_AS_PROMPT_LABEL,
    submitButtonLabel: (_j = config == null ? void 0 : config.submitButtonLabel) != null ? _j : DEFAULT_SUBMIT_BUTTON_LABEL,
    listboxLabel: (_k = config == null ? void 0 : config.listboxLabel) != null ? _k : DEFAULT_LISTBOX_LABEL
  };
}
function resolveViewConfig(config) {
  var _a, _b;
  const builder = new AISuggestionsBuilder();
  applyDefaultSuggestions(builder);
  (_a = config == null ? void 0 : config.buildAISuggestions) == null ? void 0 : _a.call(config, builder);
  return {
    placeholder: (_b = config == null ? void 0 : config.instructionPlaceholder) != null ? _b : DEFAULT_INSTRUCTION_PLACEHOLDER,
    chrome: resolveChrome(config),
    suggestions: builder.build()
  };
}
function configureAIInstructionTooltip(config) {
  return (ctx) => {
    const viewConfig = resolveViewConfig(config);
    let tooltipView = null;
    ctx.update(aiInstructionTooltipAPI.key, (api) => ({
      ...api,
      show: (from, to) => {
        tooltipView == null ? void 0 : tooltipView.show(from, to);
      }
    }));
    ctx.set(aiInstructionTooltip.key, {
      view: (view) => {
        tooltipView = new AIInstructionTooltipView(ctx, view, viewConfig);
        return tooltipView;
      }
    });
  };
}

var __typeError$7 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$7 = (obj, member, msg) => member.has(obj) || __typeError$7("Cannot " + msg);
var __privateGet$7 = (obj, member, getter) => (__accessCheck$7(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$7 = (obj, member, value) => member.has(obj) ? __typeError$7("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$7 = (obj, member, value, setter) => (__accessCheck$7(obj, member, "write to private field"), member.set(obj, value), value);
var _spinner, _label, _fallbackLabel, _start, _lastLabelText, _rafId, _tick;
const CLASS_PREFIX = "milkdown-ai-streaming";
const SPINNER_PERIOD_MS = 800;
const DEFAULT_STREAMING_FALLBACK_LABEL = "Generating";
const DEFAULT_STREAMING_CANCEL_HINT = "Esc to cancel";
const indicatorKey = new PluginKey(
  "CREPE_AI_STREAMING_INDICATOR"
);
class IndicatorWidget {
  constructor(ctx, fallbackLabel, cancelHint) {
    __privateAdd$7(this, _spinner);
    __privateAdd$7(this, _label);
    __privateAdd$7(this, _fallbackLabel);
    __privateAdd$7(this, _start, performance.now());
    __privateAdd$7(this, _lastLabelText, "");
    __privateAdd$7(this, _rafId, 0);
    __privateAdd$7(this, _tick, () => {
      const elapsed = performance.now() - __privateGet$7(this, _start);
      const angle = elapsed / SPINNER_PERIOD_MS * 360;
      __privateGet$7(this, _spinner).style.transform = `rotate(${angle}deg)`;
      __privateSet$7(this, _rafId, requestAnimationFrame(__privateGet$7(this, _tick)));
    });
    __privateSet$7(this, _fallbackLabel, fallbackLabel);
    const dom = document.createElement("span");
    dom.className = `${CLASS_PREFIX}-indicator`;
    dom.contentEditable = "false";
    dom.setAttribute("role", "status");
    dom.setAttribute("aria-live", "polite");
    const spinner = document.createElement("span");
    spinner.className = `${CLASS_PREFIX}-spinner`;
    spinner.setAttribute("aria-hidden", "true");
    dom.appendChild(spinner);
    const label = document.createElement("span");
    label.className = `${CLASS_PREFIX}-label`;
    dom.appendChild(label);
    const escHint = document.createElement("span");
    escHint.className = `${CLASS_PREFIX}-esc`;
    escHint.textContent = cancelHint;
    dom.appendChild(escHint);
    this.dom = dom;
    __privateSet$7(this, _spinner, spinner);
    __privateSet$7(this, _label, label);
    this.setLabel(ctx);
    __privateGet$7(this, _tick).call(this);
  }
  setLabel(ctx) {
    const session = ctx.get(aiSessionCtx.key);
    const text = `${session.label || __privateGet$7(this, _fallbackLabel)}\u2026`;
    if (text === __privateGet$7(this, _lastLabelText)) return;
    __privateSet$7(this, _lastLabelText, text);
    __privateGet$7(this, _label).textContent = text;
  }
  destroy() {
    if (__privateGet$7(this, _rafId)) cancelAnimationFrame(__privateGet$7(this, _rafId));
    __privateSet$7(this, _rafId, 0);
  }
}
_spinner = new WeakMap();
_label = new WeakMap();
_fallbackLabel = new WeakMap();
_start = new WeakMap();
_lastLabelText = new WeakMap();
_rafId = new WeakMap();
_tick = new WeakMap();
function streamingIndicatorPlugin(options = {}) {
  var _a, _b;
  const { config } = options;
  const fallbackLabel = (_a = config == null ? void 0 : config.fallbackLabel) != null ? _a : DEFAULT_STREAMING_FALLBACK_LABEL;
  const cancelHint = (_b = config == null ? void 0 : config.cancelHint) != null ? _b : DEFAULT_STREAMING_CANCEL_HINT;
  return $prose((ctx) => {
    let widget = null;
    function ensureWidget() {
      if (!widget) widget = new IndicatorWidget(ctx, fallbackLabel, cancelHint);
      else widget.setLabel(ctx);
      return widget;
    }
    function dropWidget() {
      if (widget) {
        widget.destroy();
        widget = null;
      }
    }
    function buildSet(doc, insertEndPos) {
      const pos = Math.min(insertEndPos, doc.content.size);
      const decoration = Decoration.widget(pos, ensureWidget().dom, {
        side: 1,
        key: "ai-streaming-indicator"
      });
      return DecorationSet.create(doc, [decoration]);
    }
    return new Plugin({
      key: indicatorKey,
      state: {
        init: () => DecorationSet.empty,
        apply(tr, decorations, _oldState, newState) {
          const streaming = streamingPluginKey.getState(newState);
          if (!(streaming == null ? void 0 : streaming.active) || streaming.insertEndPos == null) {
            dropWidget();
            return DecorationSet.empty;
          }
          if (tr.getMeta(streamingPluginKey) || tr.docChanged) {
            return buildSet(newState.doc, streaming.insertEndPos);
          }
          return decorations.map(tr.mapping, tr.doc);
        }
      },
      view() {
        return {
          destroy() {
            dropWidget();
          }
        };
      },
      props: {
        decorations(state) {
          var _a2;
          return (_a2 = indicatorKey.getState(state)) != null ? _a2 : DecorationSet.empty;
        },
        handleKeyDown(view, event) {
          var _a2;
          if (event.key !== "Escape") return false;
          const commands = ctx.get(commandsCtx);
          if (ctx.get(aiSessionCtx.key).abortController) {
            event.preventDefault();
            commands.call(abortAICmd.key, { keep: true });
            return true;
          }
          if ((_a2 = streamingPluginKey.getState(view.state)) == null ? void 0 : _a2.active) {
            event.preventDefault();
            commands.call(abortStreamingCmd.key, { keep: true });
            return true;
          }
          return false;
        }
      }
    });
  });
}

const CREPE_CUSTOM_BLOCK_TYPES = ["table", "image-block", "code_block"];
const CREPE_IGNORE_ATTRS = { heading: ["id"] };
const ai = (editor, config) => {
  var _a, _b;
  const diffCfg = (_a = config == null ? void 0 : config.diff) != null ? _a : {};
  const streamingCfg = (_b = config == null ? void 0 : config.streaming) != null ? _b : {};
  editor.config(crepeFeatureConfig(CrepeFeature.AI)).config((ctx) => {
    ctx.update(diffConfig.key, (prev) => {
      var _a2;
      return {
        ...prev,
        ignoreAttrs: (_a2 = diffCfg.ignoreAttrs) != null ? _a2 : CREPE_IGNORE_ATTRS
      };
    });
    const { ignoreAttrs: _, ...componentConfig } = diffCfg;
    ctx.update(diffComponentConfig.key, (prev) => {
      var _a2;
      return {
        ...prev,
        customBlockTypes: (_a2 = componentConfig.customBlockTypes) != null ? _a2 : CREPE_CUSTOM_BLOCK_TYPES,
        ...componentConfig
      };
    });
  }).use(diff).use(diffComponent).config((ctx) => {
    ctx.update(streamingConfig.key, (prev) => {
      var _a2;
      return {
        ...prev,
        ...streamingCfg,
        ignoreAttrs: (_a2 = streamingCfg.ignoreAttrs) != null ? _a2 : CREPE_IGNORE_ATTRS,
        // Wire diffReviewOnEnd into the streaming plugin so manual
        // endStreamingCmd calls (outside runAICmd) also respect it.
        // Only override if the user explicitly set it — otherwise
        // keep the streaming plugin's own default so tests and
        // manual-streaming use cases aren't surprised.
        ...(config == null ? void 0 : config.diffReviewOnEnd) !== void 0 ? { diffReviewOnEnd: config.diffReviewOnEnd } : {}
      };
    });
  }).use(streaming).config((ctx) => {
    ctx.update(aiProviderConfig.key, (prev) => {
      var _a2;
      return {
        ...prev,
        ...(config == null ? void 0 : config.provider) !== void 0 ? { provider: config.provider } : {},
        ...(config == null ? void 0 : config.buildContext) !== void 0 ? { buildContext: config.buildContext } : {},
        diffReviewOnEnd: (_a2 = config == null ? void 0 : config.diffReviewOnEnd) != null ? _a2 : prev.diffReviewOnEnd,
        ...(config == null ? void 0 : config.onError) !== void 0 ? { onError: config.onError } : {},
        ...(config == null ? void 0 : config.aiIcon) !== void 0 ? { aiIcon: config.aiIcon } : {}
      };
    });
  }).use(aiProviderConfig).use(aiSessionCtx).use(runAICmd).use(abortAICmd).config(configureAIInstructionTooltip(config)).use(aiInstructionTooltipAPI).use(aiInstructionTooltip).use(streamingIndicatorPlugin({ config: config == null ? void 0 : config.streamingIndicator })).use(
    diffActionsPanelPlugin({
      config: config == null ? void 0 : config.diffActions,
      enterKeyIcon: config == null ? void 0 : config.enterKeyIcon
    })
  );
};

function isInCodeBlock(selection) {
  const type = selection.$from.parent.type;
  return type.name === "code_block";
}
function isInList(selection) {
  var _a;
  const type = (_a = selection.$from.node(selection.$from.depth - 1)) == null ? void 0 : _a.type;
  return (type == null ? void 0 : type.name) === "list_item";
}

var __typeError$6 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$6 = (obj, member, msg) => member.has(obj) || __typeError$6("Cannot " + msg);
var __privateGet$6 = (obj, member, getter) => (__accessCheck$6(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$6 = (obj, member, value) => member.has(obj) ? __typeError$6("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$6 = (obj, member, value, setter) => (__accessCheck$6(obj, member, "write to private field"), member.set(obj, value), value);
var _groups, _getGroupInstance;
class GroupBuilder {
  constructor() {
    __privateAdd$6(this, _groups, []);
    this.clear = () => {
      __privateSet$6(this, _groups, []);
      return this;
    };
    __privateAdd$6(this, _getGroupInstance, (group) => {
      const groupInstance = {
        group,
        addItem: (key, item) => {
          const data = { ...item, key };
          group.items.push(data);
          return groupInstance;
        },
        clear: () => {
          group.items = [];
          return groupInstance;
        }
      };
      return groupInstance;
    });
    this.addGroup = (key, label) => {
      const items = [];
      const group = {
        key,
        label,
        items
      };
      __privateGet$6(this, _groups).push(group);
      return __privateGet$6(this, _getGroupInstance).call(this, group);
    };
    this.getGroup = (key) => {
      const group = __privateGet$6(this, _groups).find((group2) => group2.key === key);
      if (!group) throw new Error(`Group with key ${key} not found`);
      return __privateGet$6(this, _getGroupInstance).call(this, group);
    };
    this.build = () => {
      return __privateGet$6(this, _groups);
    };
  }
}
_groups = new WeakMap();
_getGroupInstance = new WeakMap();

function getGroups$2(filter, config, ctx) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G, _H, _I, _J, _K, _L, _M, _N, _O, _P, _Q, _R, _S, _T, _U, _V, _W, _X, _Y, _Z, __, _$, _aa, _ba, _ca, _da, _ea, _fa, _ga, _ha, _ia, _ja, _ka, _la, _ma, _na, _oa, _pa, _qa, _ra, _sa, _ta, _ua, _va, _wa, _xa, _ya, _za, _Aa, _Ba, _Ca, _Da, _Ea, _Fa, _Ga, _Ha, _Ia, _Ja, _Ka, _La, _Ma, _Na, _Oa, _Pa, _Qa, _Ra, _Sa, _Ta, _Ua, _Va, _Wa, _Xa, _Ya, _Za, __a, _$a, _ab, _bb, _cb, _db, _eb, _fb, _gb, _hb, _ib, _jb, _kb;
  const flags = ctx && useCrepeFeatures(ctx).get();
  const isLatexEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.Latex);
  const isImageBlockEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.ImageBlock);
  const isTableEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.Table);
  const groupBuilder = new GroupBuilder();
  if ((config == null ? void 0 : config.textGroup) !== null) {
    const textGroup = groupBuilder.addGroup(
      "text",
      (_b = (_a = config == null ? void 0 : config.textGroup) == null ? void 0 : _a.label) != null ? _b : "Text"
    );
    if (((_c = config == null ? void 0 : config.textGroup) == null ? void 0 : _c.text) !== null) {
      textGroup.addItem("text", {
        label: (_f = (_e = (_d = config == null ? void 0 : config.textGroup) == null ? void 0 : _d.text) == null ? void 0 : _e.label) != null ? _f : "Text",
        icon: (_i = (_h = (_g = config == null ? void 0 : config.textGroup) == null ? void 0 : _g.text) == null ? void 0 : _h.icon) != null ? _i : textIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const paragraph = paragraphSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: paragraph
          });
        }
      });
    }
    if (((_j = config == null ? void 0 : config.textGroup) == null ? void 0 : _j.h1) !== null) {
      textGroup.addItem("h1", {
        label: (_m = (_l = (_k = config == null ? void 0 : config.textGroup) == null ? void 0 : _k.h1) == null ? void 0 : _l.label) != null ? _m : "Heading 1",
        icon: (_p = (_o = (_n = config == null ? void 0 : config.textGroup) == null ? void 0 : _n.h1) == null ? void 0 : _o.icon) != null ? _p : h1Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 1
            }
          });
        }
      });
    }
    if (((_q = config == null ? void 0 : config.textGroup) == null ? void 0 : _q.h2) !== null) {
      textGroup.addItem("h2", {
        label: (_t = (_s = (_r = config == null ? void 0 : config.textGroup) == null ? void 0 : _r.h2) == null ? void 0 : _s.label) != null ? _t : "Heading 2",
        icon: (_w = (_v = (_u = config == null ? void 0 : config.textGroup) == null ? void 0 : _u.h2) == null ? void 0 : _v.icon) != null ? _w : h2Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 2
            }
          });
        }
      });
    }
    if (((_x = config == null ? void 0 : config.textGroup) == null ? void 0 : _x.h3) !== null) {
      textGroup.addItem("h3", {
        label: (_A = (_z = (_y = config == null ? void 0 : config.textGroup) == null ? void 0 : _y.h3) == null ? void 0 : _z.label) != null ? _A : "Heading 3",
        icon: (_D = (_C = (_B = config == null ? void 0 : config.textGroup) == null ? void 0 : _B.h3) == null ? void 0 : _C.icon) != null ? _D : h3Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 3
            }
          });
        }
      });
    }
    if (((_E = config == null ? void 0 : config.textGroup) == null ? void 0 : _E.h4) !== null) {
      textGroup.addItem("h4", {
        label: (_H = (_G = (_F = config == null ? void 0 : config.textGroup) == null ? void 0 : _F.h4) == null ? void 0 : _G.label) != null ? _H : "Heading 4",
        icon: (_K = (_J = (_I = config == null ? void 0 : config.textGroup) == null ? void 0 : _I.h4) == null ? void 0 : _J.icon) != null ? _K : h4Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 4
            }
          });
        }
      });
    }
    if (((_L = config == null ? void 0 : config.textGroup) == null ? void 0 : _L.h5) !== null) {
      textGroup.addItem("h5", {
        label: (_O = (_N = (_M = config == null ? void 0 : config.textGroup) == null ? void 0 : _M.h5) == null ? void 0 : _N.label) != null ? _O : "Heading 5",
        icon: (_R = (_Q = (_P = config == null ? void 0 : config.textGroup) == null ? void 0 : _P.h5) == null ? void 0 : _Q.icon) != null ? _R : h5Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 5
            }
          });
        }
      });
    }
    if (((_S = config == null ? void 0 : config.textGroup) == null ? void 0 : _S.h6) !== null) {
      textGroup.addItem("h6", {
        label: (_V = (_U = (_T = config == null ? void 0 : config.textGroup) == null ? void 0 : _T.h6) == null ? void 0 : _U.label) != null ? _V : "Heading 6",
        icon: (_Y = (_X = (_W = config == null ? void 0 : config.textGroup) == null ? void 0 : _W.h6) == null ? void 0 : _X.icon) != null ? _Y : h6Icon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const heading = headingSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: heading,
            attrs: {
              level: 6
            }
          });
        }
      });
    }
    if (((_Z = config == null ? void 0 : config.textGroup) == null ? void 0 : _Z.quote) !== null) {
      textGroup.addItem("quote", {
        label: (_aa = (_$ = (__ = config == null ? void 0 : config.textGroup) == null ? void 0 : __.quote) == null ? void 0 : _$.label) != null ? _aa : "Quote",
        icon: (_da = (_ca = (_ba = config == null ? void 0 : config.textGroup) == null ? void 0 : _ba.quote) == null ? void 0 : _ca.icon) != null ? _da : quoteIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const blockquote = blockquoteSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(wrapInBlockTypeCommand.key, {
            nodeType: blockquote
          });
        }
      });
    }
    if (((_ea = config == null ? void 0 : config.textGroup) == null ? void 0 : _ea.divider) !== null) {
      textGroup.addItem("divider", {
        label: (_ha = (_ga = (_fa = config == null ? void 0 : config.textGroup) == null ? void 0 : _fa.divider) == null ? void 0 : _ga.label) != null ? _ha : "Divider",
        icon: (_ka = (_ja = (_ia = config == null ? void 0 : config.textGroup) == null ? void 0 : _ia.divider) == null ? void 0 : _ja.icon) != null ? _ka : dividerIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const hr = hrSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(addBlockTypeCommand.key, {
            nodeType: hr
          });
        }
      });
    }
  }
  if ((config == null ? void 0 : config.listGroup) !== null) {
    const listGroup = groupBuilder.addGroup(
      "list",
      (_ma = (_la = config == null ? void 0 : config.listGroup) == null ? void 0 : _la.label) != null ? _ma : "List"
    );
    if (((_na = config == null ? void 0 : config.listGroup) == null ? void 0 : _na.bulletList) !== null) {
      listGroup.addItem("bullet-list", {
        label: (_qa = (_pa = (_oa = config == null ? void 0 : config.listGroup) == null ? void 0 : _oa.bulletList) == null ? void 0 : _pa.label) != null ? _qa : "Bullet List",
        icon: (_ta = (_sa = (_ra = config == null ? void 0 : config.listGroup) == null ? void 0 : _ra.bulletList) == null ? void 0 : _sa.icon) != null ? _ta : bulletListIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const bulletList = bulletListSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(wrapInBlockTypeCommand.key, {
            nodeType: bulletList
          });
        }
      });
    }
    if (((_ua = config == null ? void 0 : config.listGroup) == null ? void 0 : _ua.orderedList) !== null) {
      listGroup.addItem("ordered-list", {
        label: (_xa = (_wa = (_va = config == null ? void 0 : config.listGroup) == null ? void 0 : _va.orderedList) == null ? void 0 : _wa.label) != null ? _xa : "Ordered List",
        icon: (_Aa = (_za = (_ya = config == null ? void 0 : config.listGroup) == null ? void 0 : _ya.orderedList) == null ? void 0 : _za.icon) != null ? _Aa : orderedListIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const orderedList = orderedListSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(wrapInBlockTypeCommand.key, {
            nodeType: orderedList
          });
        }
      });
    }
    if (((_Ba = config == null ? void 0 : config.listGroup) == null ? void 0 : _Ba.taskList) !== null) {
      listGroup.addItem("task-list", {
        label: (_Ea = (_Da = (_Ca = config == null ? void 0 : config.listGroup) == null ? void 0 : _Ca.taskList) == null ? void 0 : _Da.label) != null ? _Ea : "Task List",
        icon: (_Ha = (_Ga = (_Fa = config == null ? void 0 : config.listGroup) == null ? void 0 : _Fa.taskList) == null ? void 0 : _Ga.icon) != null ? _Ha : todoListIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const listItem = listItemSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(wrapInBlockTypeCommand.key, {
            nodeType: listItem,
            attrs: { checked: false }
          });
        }
      });
    }
  }
  if ((config == null ? void 0 : config.advancedGroup) !== null) {
    const advancedGroup = groupBuilder.addGroup(
      "advanced",
      (_Ja = (_Ia = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Ia.label) != null ? _Ja : "Advanced"
    );
    if (((_Ka = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Ka.image) !== null && isImageBlockEnabled) {
      advancedGroup.addItem("image", {
        label: (_Na = (_Ma = (_La = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _La.image) == null ? void 0 : _Ma.label) != null ? _Na : "Image",
        icon: (_Qa = (_Pa = (_Oa = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Oa.image) == null ? void 0 : _Pa.icon) != null ? _Qa : imageIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const imageBlock = imageBlockSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(addBlockTypeCommand.key, {
            nodeType: imageBlock
          });
        }
      });
    }
    if (((_Ra = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Ra.codeBlock) !== null) {
      advancedGroup.addItem("code", {
        label: (_Ua = (_Ta = (_Sa = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Sa.codeBlock) == null ? void 0 : _Ta.label) != null ? _Ua : "Code",
        icon: (_Xa = (_Wa = (_Va = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Va.codeBlock) == null ? void 0 : _Wa.icon) != null ? _Xa : codeIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const codeBlock = codeBlockSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(setBlockTypeCommand.key, {
            nodeType: codeBlock
          });
        }
      });
    }
    if (((_Ya = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Ya.table) !== null && isTableEnabled) {
      advancedGroup.addItem("table", {
        label: (_$a = (__a = (_Za = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _Za.table) == null ? void 0 : __a.label) != null ? _$a : "Table",
        icon: (_cb = (_bb = (_ab = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _ab.table) == null ? void 0 : _bb.icon) != null ? _cb : tableIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const view = ctx2.get(editorViewCtx);
          commands.call(clearTextInCurrentBlockCommand.key);
          const { from } = view.state.selection;
          commands.call(addBlockTypeCommand.key, {
            nodeType: createTable(ctx2, 3, 3)
          });
          commands.call(selectTextNearPosCommand.key, {
            pos: from
          });
        }
      });
    }
    if (((_db = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _db.math) !== null && isLatexEnabled) {
      advancedGroup.addItem("math", {
        label: (_gb = (_fb = (_eb = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _eb.math) == null ? void 0 : _fb.label) != null ? _gb : "Math",
        icon: (_jb = (_ib = (_hb = config == null ? void 0 : config.advancedGroup) == null ? void 0 : _hb.math) == null ? void 0 : _ib.icon) != null ? _jb : functionsIcon,
        onRun: (ctx2) => {
          const commands = ctx2.get(commandsCtx);
          const codeBlock = codeBlockSchema.type(ctx2);
          commands.call(clearTextInCurrentBlockCommand.key);
          commands.call(addBlockTypeCommand.key, {
            nodeType: codeBlock,
            attrs: { language: "LaTeX" }
          });
        }
      });
    }
  }
  (_kb = config == null ? void 0 : config.buildMenu) == null ? void 0 : _kb.call(config, groupBuilder);
  let groups = groupBuilder.build();
  if (filter) {
    groups = groups.map((group) => {
      const items2 = group.items.filter(
        (item) => item.label.toLowerCase().includes(filter.toLowerCase())
      );
      return {
        ...group,
        items: items2
      };
    }).filter((group) => group.items.length > 0);
  }
  const items = groups.flatMap((groups2) => groups2.items);
  items.forEach((item, index) => {
    Object.assign(item, { index });
  });
  groups.reduce((acc, group) => {
    const end = acc + group.items.length;
    Object.assign(group, {
      range: [acc, end]
    });
    return end;
  }, 0);
  return {
    groups,
    size: items.length
  };
}

keepAlive(h);
const Menu = defineComponent({
  props: {
    ctx: {
      type: Object,
      required: true
    },
    show: {
      type: Object,
      required: true
    },
    filter: {
      type: Object,
      required: true
    },
    hide: {
      type: Function,
      required: true
    },
    config: {
      type: Object,
      required: false
    }
  },
  setup({ ctx, show, filter, hide, config }) {
    const host = ref();
    const groupInfo = computed(() => getGroups$2(filter.value, config, ctx));
    const hoverIndex = ref(0);
    const prevMousePosition = ref({ x: -999, y: -999 });
    const onPointerMove = (e) => {
      const { x, y } = e;
      prevMousePosition.value = { x, y };
    };
    watch([groupInfo, show], () => {
      const { size } = groupInfo.value;
      if (size === 0 && show.value) hide();
      else if (hoverIndex.value >= size) hoverIndex.value = 0;
    });
    const onHover = (index, after) => {
      const prevHoverIndex = hoverIndex.value;
      const next = typeof index === "function" ? index(prevHoverIndex) : index;
      after == null ? void 0 : after(next);
      hoverIndex.value = next;
    };
    const scrollToIndex = (index) => {
      var _a, _b;
      const target = (_a = host.value) == null ? void 0 : _a.querySelector(
        `[data-index="${index}"]`
      );
      const scrollRoot = (_b = host.value) == null ? void 0 : _b.querySelector(".menu-groups");
      if (!target || !scrollRoot) return;
      scrollRoot.scrollTop = target.offsetTop - scrollRoot.offsetTop;
    };
    const runByIndex = (index) => {
      const item = groupInfo.value.groups.flatMap((group) => group.items).at(index);
      if ((item == null ? void 0 : item.onRun) && ctx) item.onRun(ctx);
      hide();
    };
    const onKeydown = (e) => {
      const { size, groups } = groupInfo.value;
      if (e.key === "Escape") {
        e.preventDefault();
        hide == null ? void 0 : hide();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        return onHover(
          (index) => index < size - 1 ? index + 1 : index,
          scrollToIndex
        );
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        return onHover(
          (index) => index <= 0 ? index : index - 1,
          scrollToIndex
        );
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        return onHover((index) => {
          const group = groups.find(
            (group2) => group2.range[0] <= index && group2.range[1] > index
          );
          if (!group) return index;
          const prevGroup = groups[groups.indexOf(group) - 1];
          if (!prevGroup) return index;
          return prevGroup.range[1] - 1;
        }, scrollToIndex);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        return onHover((index) => {
          const group = groups.find(
            (group2) => group2.range[0] <= index && group2.range[1] > index
          );
          if (!group) return index;
          const nextGroup = groups[groups.indexOf(group) + 1];
          if (!nextGroup) return index;
          return nextGroup.range[0];
        }, scrollToIndex);
      }
      if (e.key === "Enter") {
        e.preventDefault();
        runByIndex(hoverIndex.value);
      }
    };
    const getOnPointerEnter = (index) => (e) => {
      const prevPos = prevMousePosition.value;
      if (!prevPos) return;
      const { x, y } = e;
      if (x === prevPos.x && y === prevPos.y) return;
      onHover(index);
    };
    watchEffect(() => {
      const isShown = show.value;
      if (isShown) {
        window.addEventListener("keydown", onKeydown, { capture: true });
      } else {
        window.removeEventListener("keydown", onKeydown, { capture: true });
      }
    });
    onUnmounted(() => {
      window.removeEventListener("keydown", onKeydown, { capture: true });
    });
    return () => {
      return /* @__PURE__ */ h("div", { ref: host, onPointerdown: (e) => e.preventDefault() }, /* @__PURE__ */ h("nav", { class: "tab-group" }, /* @__PURE__ */ h("ul", null, groupInfo.value.groups.map((group) => /* @__PURE__ */ h(
        "li",
        {
          key: group.key,
          onPointerdown: () => onHover(group.range[0], scrollToIndex),
          class: hoverIndex.value >= group.range[0] && hoverIndex.value < group.range[1] ? "selected" : ""
        },
        group.label
      )))), /* @__PURE__ */ h("div", { class: "menu-groups", onPointermove: onPointerMove }, groupInfo.value.groups.map((group) => /* @__PURE__ */ h("div", { key: group.key, class: "menu-group" }, /* @__PURE__ */ h("h6", null, group.label), /* @__PURE__ */ h("ul", null, group.items.map((item) => /* @__PURE__ */ h(
        "li",
        {
          key: item.key,
          "data-index": item.index,
          class: hoverIndex.value === item.index ? "hover" : "",
          onPointerenter: getOnPointerEnter(item.index),
          onPointerdown: () => {
            var _a, _b;
            (_b = (_a = host.value) == null ? void 0 : _a.querySelector(`[data-index="${item.index}"]`)) == null ? void 0 : _b.classList.add("active");
          },
          onPointerup: () => {
            var _a, _b;
            (_b = (_a = host.value) == null ? void 0 : _a.querySelector(`[data-index="${item.index}"]`)) == null ? void 0 : _b.classList.remove("active");
            runByIndex(item.index);
          }
        },
        /* @__PURE__ */ h(Icon, { icon: item.icon }),
        /* @__PURE__ */ h("span", null, item.label)
      )))))));
    };
  }
});

var __typeError$5 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$5 = (obj, member, msg) => member.has(obj) || __typeError$5("Cannot " + msg);
var __privateGet$5 = (obj, member, getter) => (__accessCheck$5(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$5 = (obj, member, value) => member.has(obj) ? __typeError$5("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$5 = (obj, member, value, setter) => (__accessCheck$5(obj, member, "write to private field"), member.set(obj, value), value);
var _content$3, _app$4, _filter, _slashProvider, _programmaticallyPos;
const menu = slashFactory("CREPE_MENU");
const menuAPI = $ctx(
  {
    show: () => {
    },
    hide: () => {
    }
  },
  "menuAPICtx"
);
function configureMenu(ctx, config) {
  ctx.set(menu.key, {
    view: (view) => new MenuView(ctx, view, config)
  });
}
class MenuView {
  constructor(ctx, view, config) {
    __privateAdd$5(this, _content$3);
    __privateAdd$5(this, _app$4);
    __privateAdd$5(this, _filter);
    __privateAdd$5(this, _slashProvider);
    __privateAdd$5(this, _programmaticallyPos, null);
    this.update = (view) => {
      __privateGet$5(this, _slashProvider).update(view);
    };
    this.show = (pos) => {
      __privateSet$5(this, _programmaticallyPos, pos);
      __privateGet$5(this, _filter).value = "";
      __privateGet$5(this, _slashProvider).show();
    };
    this.hide = () => {
      __privateSet$5(this, _programmaticallyPos, null);
      __privateGet$5(this, _slashProvider).hide();
    };
    this.destroy = () => {
      __privateGet$5(this, _slashProvider).destroy();
      __privateGet$5(this, _app$4).unmount();
      __privateGet$5(this, _content$3).remove();
    };
    const content = document.createElement("div");
    content.classList.add("milkdown-slash-menu");
    const show = ref(false);
    const filter = ref("");
    __privateSet$5(this, _filter, filter);
    const hide = this.hide;
    const app = createApp(Menu, {
      ctx,
      config,
      show,
      filter,
      hide
    });
    __privateSet$5(this, _app$4, app);
    app.mount(content);
    __privateSet$5(this, _content$3, content);
    const self = this;
    __privateSet$5(this, _slashProvider, new SlashProvider({
      content: __privateGet$5(this, _content$3),
      debounce: 20,
      shouldShow(view2) {
        if (isInCodeBlock(view2.state.selection) || isInList(view2.state.selection))
          return false;
        const currentText = this.getContent(
          view2,
          (node) => ["paragraph", "heading"].includes(node.type.name)
        );
        if (currentText == null) return false;
        if (!isSelectionAtEndOfNode(view2.state.selection)) {
          return false;
        }
        const pos = __privateGet$5(self, _programmaticallyPos);
        filter.value = currentText.startsWith("/") ? currentText.slice(1) : currentText;
        if (typeof pos === "number") {
          const maxSize = view2.state.doc.nodeSize - 2;
          const validPos = Math.min(pos, maxSize);
          if (view2.state.doc.resolve(validPos).node() !== view2.state.doc.resolve(view2.state.selection.from).node()) {
            __privateSet$5(self, _programmaticallyPos, null);
            return false;
          }
          return true;
        }
        if (!currentText.startsWith("/")) return false;
        return true;
      },
      offset: 10
    }));
    __privateGet$5(this, _slashProvider).onShow = () => {
      show.value = true;
    };
    __privateGet$5(this, _slashProvider).onHide = () => {
      show.value = false;
    };
    this.update(view);
    ctx.set(menuAPI.key, {
      show: (pos) => this.show(pos),
      hide: () => this.hide()
    });
  }
}
_content$3 = new WeakMap();
_app$4 = new WeakMap();
_filter = new WeakMap();
_slashProvider = new WeakMap();
_programmaticallyPos = new WeakMap();
function isSelectionAtEndOfNode(selection) {
  if (!(selection instanceof TextSelection)) return false;
  const { $head } = selection;
  const parent = $head.parent;
  const offset = $head.parentOffset;
  return offset === parent.content.size;
}

keepAlive(h, Fragment);
const BlockHandle = defineComponent({
  props: {
    onAdd: {
      type: Function,
      required: true
    },
    addIcon: {
      type: String,
      required: true
    },
    handleIcon: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const addButton = ref();
    return () => {
      return /* @__PURE__ */ h(Fragment, null, /* @__PURE__ */ h(
        "div",
        {
          ref: addButton,
          class: "operation-item",
          onPointerdown: (e) => {
            var _a;
            e.preventDefault();
            e.stopPropagation();
            (_a = addButton.value) == null ? void 0 : _a.classList.add("active");
          },
          onPointerup: (e) => {
            var _a;
            e.preventDefault();
            e.stopPropagation();
            (_a = addButton.value) == null ? void 0 : _a.classList.remove("active");
            props.onAdd();
          }
        },
        /* @__PURE__ */ h(Icon, { icon: props.addIcon })
      ), /* @__PURE__ */ h("div", { class: "operation-item" }, /* @__PURE__ */ h(Icon, { icon: props.handleIcon })));
    };
  }
});

var __typeError$4 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$4 = (obj, member, msg) => member.has(obj) || __typeError$4("Cannot " + msg);
var __privateGet$4 = (obj, member, getter) => (__accessCheck$4(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$4 = (obj, member, value) => member.has(obj) ? __typeError$4("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$4 = (obj, member, value, setter) => (__accessCheck$4(obj, member, "write to private field"), member.set(obj, value), value);
var _content$2, _provider$1, _app$3, _ctx;
class BlockHandleView {
  constructor(ctx, config) {
    __privateAdd$4(this, _content$2);
    __privateAdd$4(this, _provider$1);
    __privateAdd$4(this, _app$3);
    __privateAdd$4(this, _ctx);
    this.update = () => {
      __privateGet$4(this, _provider$1).update();
    };
    this.destroy = () => {
      __privateGet$4(this, _provider$1).destroy();
      __privateGet$4(this, _content$2).remove();
      __privateGet$4(this, _app$3).unmount();
    };
    this.onAdd = () => {
      const ctx = __privateGet$4(this, _ctx);
      const view = ctx.get(editorViewCtx);
      if (!view.hasFocus()) view.focus();
      const { state, dispatch } = view;
      const active = __privateGet$4(this, _provider$1).active;
      if (!active) return;
      const $pos = active.$pos;
      const pos = $pos.pos + active.node.nodeSize;
      let tr = state.tr.insert(pos, paragraphSchema.type(ctx).create());
      tr = tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));
      dispatch(tr.scrollIntoView());
      __privateGet$4(this, _provider$1).hide();
      ctx.get(menuAPI.key).show(tr.selection.from);
    };
    var _a, _b, _c;
    __privateSet$4(this, _ctx, ctx);
    const content = document.createElement("div");
    content.classList.add("milkdown-block-handle");
    const app = createApp(BlockHandle, {
      onAdd: this.onAdd,
      addIcon: (_a = config == null ? void 0 : config.handleAddIcon) != null ? _a : plusIcon,
      handleIcon: (_b = config == null ? void 0 : config.handleDragIcon) != null ? _b : menuIcon
    });
    app.mount(content);
    __privateSet$4(this, _app$3, app);
    __privateSet$4(this, _content$2, content);
    const blockProviderOptions = (_c = config == null ? void 0 : config.blockHandle) != null ? _c : {};
    __privateSet$4(this, _provider$1, new BlockProvider({
      ctx,
      content,
      getOffset: () => 16,
      getPlacement: ({ active, blockDom }) => {
        if (active.node.type.name === "heading") return "left";
        let totalDescendant = 0;
        active.node.descendants((node) => {
          totalDescendant += node.childCount;
        });
        const dom = active.el;
        const domRect = dom.getBoundingClientRect();
        const handleRect = blockDom.getBoundingClientRect();
        const style = window.getComputedStyle(dom);
        const paddingTop = Number.parseInt(style.paddingTop, 10) || 0;
        const paddingBottom = Number.parseInt(style.paddingBottom, 10) || 0;
        const height = domRect.height - paddingTop - paddingBottom;
        const handleHeight = handleRect.height;
        return totalDescendant > 2 || handleHeight < height ? "left-start" : "left";
      },
      ...blockProviderOptions
    }));
    this.update();
  }
}
_content$2 = new WeakMap();
_provider$1 = new WeakMap();
_app$3 = new WeakMap();
_ctx = new WeakMap();
function configureBlockHandle(ctx, config) {
  var _a, _b;
  ctx.set(blockConfig.key, {
    filterNodes: (pos) => {
      const filter = findParent(
        (node) => ["table", "blockquote", "math_inline"].includes(node.type.name)
      )(pos);
      if (filter) return false;
      return true;
    },
    mousemoveThrottle: (_b = (_a = config == null ? void 0 : config.blockConfig) == null ? void 0 : _a.mousemoveThrottle) != null ? _b : 50
  });
  ctx.set(block.key, {
    view: () => new BlockHandleView(ctx, config)
  });
}

const blockEdit = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.BlockEdit)).config((ctx) => configureBlockHandle(ctx, config)).config((ctx) => configureMenu(ctx, config)).use(menuAPI).use(block).use(menu);
};

const codeMirror = (editor, config = {}) => {
  editor.config(crepeFeatureConfig(CrepeFeature.CodeMirror)).config((ctx) => {
    const { languages = [], theme } = config;
    const extensions = [
      keymap.of(defaultKeymap.concat(indentWithTab)),
      basicSetup
    ];
    if (theme) {
      extensions.push(theme);
    }
    if (config.extensions) {
      extensions.push(...config.extensions);
    }
    ctx.update(codeBlockConfig.key, (defaultConfig) => {
      var _a;
      return {
        extensions,
        languages,
        expandIcon: config.expandIcon || chevronDownIcon,
        searchIcon: config.searchIcon || searchIcon,
        clearSearchIcon: config.clearSearchIcon || clearIcon,
        searchPlaceholder: config.searchPlaceholder || "Search language",
        copyText: config.copyText || "Copy",
        copyIcon: config.copyIcon || copyIcon,
        onCopy: config.onCopy || (() => {
        }),
        noResultText: config.noResultText || "No result",
        renderLanguage: config.renderLanguage || defaultConfig.renderLanguage,
        renderPreview: config.renderPreview || defaultConfig.renderPreview,
        previewToggleButton: (previewOnlyMode) => {
          var _a2, _b;
          const icon = ((_a2 = config.previewToggleIcon) == null ? void 0 : _a2.call(config, previewOnlyMode)) || (previewOnlyMode ? editIcon : visibilityOffIcon);
          const text = ((_b = config.previewToggleText) == null ? void 0 : _b.call(config, previewOnlyMode)) || (previewOnlyMode ? "Edit" : "Hide");
          return [icon, text].map((v) => v.trim()).join(" ");
        },
        previewLabel: config.previewLabel || defaultConfig.previewLabel,
        previewLoading: config.previewLoading || defaultConfig.previewLoading,
        previewOnlyByDefault: (_a = config.previewOnlyByDefault) != null ? _a : defaultConfig.previewOnlyByDefault
      };
    });
  }).use(codeBlockComponent);
};

const cursor = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.Cursor)).config((ctx) => {
    ctx.update(dropIndicatorConfig.key, () => {
      var _a, _b;
      return {
        class: "crepe-drop-cursor",
        width: (_a = config == null ? void 0 : config.width) != null ? _a : 4,
        color: (_b = config == null ? void 0 : config.color) != null ? _b : false
      };
    });
  }).use(cursor$1);
  if ((config == null ? void 0 : config.virtual) === false) {
    return;
  }
  const virtualCursor = createVirtualCursor();
  editor.use($prose(() => virtualCursor));
};

const imageBlock = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.ImageBlock)).config((ctx) => {
    ctx.update(inlineImageConfig.key, (value) => {
      var _a, _b, _c, _d, _e, _f;
      return {
        uploadButton: (_a = config == null ? void 0 : config.inlineUploadButton) != null ? _a : "Upload",
        imageIcon: (_b = config == null ? void 0 : config.inlineImageIcon) != null ? _b : imageIcon,
        confirmButton: (_c = config == null ? void 0 : config.inlineConfirmButton) != null ? _c : confirmIcon,
        uploadPlaceholderText: (_d = config == null ? void 0 : config.inlineUploadPlaceholderText) != null ? _d : "or paste link",
        onUpload: (_f = (_e = config == null ? void 0 : config.inlineOnUpload) != null ? _e : config == null ? void 0 : config.onUpload) != null ? _f : value.onUpload,
        proxyDomURL: config == null ? void 0 : config.proxyDomURL
      };
    });
    ctx.update(imageBlockConfig.key, (value) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i;
      return {
        uploadButton: (_a = config == null ? void 0 : config.blockUploadButton) != null ? _a : "Upload file",
        imageIcon: (_b = config == null ? void 0 : config.blockImageIcon) != null ? _b : imageIcon,
        captionIcon: (_c = config == null ? void 0 : config.blockCaptionIcon) != null ? _c : captionIcon,
        confirmButton: (_d = config == null ? void 0 : config.blockConfirmButton) != null ? _d : "Confirm",
        captionPlaceholderText: (_e = config == null ? void 0 : config.blockCaptionPlaceholderText) != null ? _e : "Write Image Caption",
        uploadPlaceholderText: (_f = config == null ? void 0 : config.blockUploadPlaceholderText) != null ? _f : "or paste link",
        onUpload: (_h = (_g = config == null ? void 0 : config.blockOnUpload) != null ? _g : config == null ? void 0 : config.onUpload) != null ? _h : value.onUpload,
        proxyDomURL: config == null ? void 0 : config.proxyDomURL,
        onImageLoadError: (_i = config == null ? void 0 : config.onImageLoadError) != null ? _i : value.onImageLoadError,
        maxWidth: config == null ? void 0 : config.maxWidth,
        maxHeight: config == null ? void 0 : config.maxHeight
      };
    });
  }).use(imageBlockComponent).use(imageInlineComponent);
};

const blockLatexSchema = codeBlockSchema.extendSchema((prev) => {
  return (ctx) => {
    const baseSchema = prev(ctx);
    return {
      ...baseSchema,
      toMarkdown: {
        match: baseSchema.toMarkdown.match,
        runner: (state, node) => {
          var _a, _b;
          const language = (_a = node.attrs.language) != null ? _a : "";
          if (language.toLowerCase() === "latex") {
            state.addNode(
              "math",
              void 0,
              ((_b = node.content.firstChild) == null ? void 0 : _b.text) || ""
            );
          } else {
            return baseSchema.toMarkdown.runner(state, node);
          }
        }
      }
    };
  };
});

const mathInlineId = "math_inline";
const toggleLatexCommandName = "ToggleLatex";

const mathInlineSchema = $nodeSchema(mathInlineId, () => ({
  group: "inline",
  inline: true,
  draggable: true,
  atom: true,
  attrs: {
    value: {
      default: ""
    }
  },
  parseDOM: [
    {
      tag: `span[data-type="${mathInlineId}"]`,
      getAttrs: (dom) => {
        var _a;
        return {
          value: (_a = dom.dataset.value) != null ? _a : ""
        };
      }
    }
  ],
  toDOM: (node) => {
    const code = node.attrs.value;
    const dom = document.createElement("span");
    dom.dataset.type = mathInlineId;
    dom.dataset.value = code;
    katex.render(code, dom, {
      throwOnError: false
    });
    return dom;
  },
  parseMarkdown: {
    match: (node) => node.type === "inlineMath",
    runner: (state, node, type) => {
      state.addNode(type, { value: node.value });
    }
  },
  toMarkdown: {
    match: (node) => node.type.name === mathInlineId,
    runner: (state, node) => {
      state.addNode("inlineMath", void 0, node.attrs.value);
    }
  }
}));

const toggleLatexCommand = $command("ToggleLatex", (ctx) => {
  return () => (state, dispatch) => {
    const {
      hasNode: hasLatex,
      pos: latexPos,
      target: latexNode
    } = findNodeInSelection(state, mathInlineSchema.type(ctx));
    const { selection, doc, tr } = state;
    if (!hasLatex) {
      const text = doc.textBetween(selection.from, selection.to);
      let _tr2 = tr.replaceSelectionWith(
        mathInlineSchema.type(ctx).create({
          value: text
        })
      );
      if (dispatch) {
        dispatch(
          _tr2.setSelection(NodeSelection.create(_tr2.doc, selection.from))
        );
      }
      return true;
    }
    const { from, to } = selection;
    if (!latexNode || latexPos < 0) return false;
    let _tr = tr.delete(latexPos, latexPos + 1);
    const content = latexNode.attrs.value;
    _tr = _tr.insertText(content, latexPos);
    if (dispatch) {
      dispatch(
        _tr.setSelection(
          TextSelection.create(_tr.doc, from, to + content.length - 1)
        )
      );
    }
    return true;
  };
});

const inlineLatexTooltip = tooltipFactory("INLINE_LATEX");

keepAlive(h);
const LatexTooltip = defineComponent({
  props: {
    config: {
      type: Object,
      required: true
    },
    innerView: {
      type: Object,
      required: true
    },
    updateValue: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const innerViewRef = (el) => {
      if (!el || !(el instanceof HTMLElement)) return;
      while (el.firstChild) {
        el.removeChild(el.firstChild);
      }
      if (props.innerView.value) {
        el.appendChild(props.innerView.value.dom);
      }
    };
    const onUpdate = (e) => {
      e.preventDefault();
      props.updateValue.value();
    };
    return () => {
      return /* @__PURE__ */ h("div", { class: "container" }, props.innerView && /* @__PURE__ */ h("div", { ref: innerViewRef }), /* @__PURE__ */ h("button", { type: "button", onPointerdown: onUpdate }, /* @__PURE__ */ h(Icon, { icon: props.config.inlineEditConfirm })));
    };
  }
});

var __typeError$3 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$3 = (obj, member, msg) => member.has(obj) || __typeError$3("Cannot " + msg);
var __privateGet$3 = (obj, member, getter) => (__accessCheck$3(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$3 = (obj, member, value) => member.has(obj) ? __typeError$3("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$3 = (obj, member, value, setter) => (__accessCheck$3(obj, member, "write to private field"), member.set(obj, value), value);
var _content$1, _provider, _dom, _innerView, _updateValue, _app$2, _onHide, _shouldShow;
class LatexInlineTooltip {
  constructor(ctx, view, config) {
    this.ctx = ctx;
    __privateAdd$3(this, _content$1);
    __privateAdd$3(this, _provider);
    __privateAdd$3(this, _dom);
    __privateAdd$3(this, _innerView, shallowRef(null));
    __privateAdd$3(this, _updateValue, shallowRef(() => {
    }));
    __privateAdd$3(this, _app$2);
    __privateAdd$3(this, _onHide, () => {
      if (__privateGet$3(this, _innerView).value) {
        __privateGet$3(this, _innerView).value.destroy();
        __privateGet$3(this, _innerView).value = null;
      }
    });
    __privateAdd$3(this, _shouldShow, (view) => {
      const shouldShow = () => {
        if (!view.editable) return false;
        const { selection, schema } = view.state;
        if (selection.empty) return false;
        if (!(selection instanceof NodeSelection)) return false;
        const node = selection.node;
        if (node.type.name !== mathInlineId) return false;
        const textFrom = selection.from;
        const paragraph = schema.nodes.paragraph.create(
          null,
          schema.text(node.attrs.value)
        );
        const innerView = new EditorView(__privateGet$3(this, _dom), {
          state: EditorState.create({
            doc: paragraph,
            schema: new Schema({
              nodes: {
                doc: {
                  content: "block+"
                },
                paragraph: {
                  content: "inline*",
                  group: "block",
                  parseDOM: [{ tag: "p" }],
                  toDOM() {
                    return ["p", 0];
                  }
                },
                text: {
                  group: "inline"
                }
              }
            }),
            plugins: [
              keymap$1({
                "Mod-z": undo,
                "Mod-Z": redo,
                "Mod-y": redo,
                Enter: () => {
                  __privateGet$3(this, _updateValue).value();
                  return true;
                }
              })
            ]
          })
        });
        __privateGet$3(this, _innerView).value = innerView;
        __privateGet$3(this, _updateValue).value = () => {
          const { tr } = view.state;
          tr.setNodeAttribute(textFrom, "value", innerView.state.doc.textContent);
          view.dispatch(tr);
          requestAnimationFrame(() => {
            view.focus();
          });
        };
        return true;
      };
      const show = shouldShow();
      if (!show) __privateGet$3(this, _onHide).call(this);
      return show;
    });
    this.update = (view, prevState) => {
      __privateGet$3(this, _provider).update(view, prevState);
    };
    this.destroy = () => {
      __privateGet$3(this, _app$2).unmount();
      __privateGet$3(this, _provider).destroy();
      __privateGet$3(this, _content$1).remove();
    };
    const content = document.createElement("div");
    content.className = "milkdown-latex-inline-edit";
    __privateSet$3(this, _content$1, content);
    __privateSet$3(this, _app$2, createApp(LatexTooltip, {
      config,
      innerView: __privateGet$3(this, _innerView),
      updateValue: __privateGet$3(this, _updateValue)
    }));
    __privateGet$3(this, _app$2).mount(content);
    __privateSet$3(this, _provider, new TooltipProvider({
      debounce: 0,
      content: __privateGet$3(this, _content$1),
      shouldShow: __privateGet$3(this, _shouldShow),
      offset: 10,
      floatingUIOptions: {
        placement: "bottom"
      }
    }));
    __privateGet$3(this, _provider).update(view);
    __privateSet$3(this, _dom, document.createElement("div"));
  }
}
_content$1 = new WeakMap();
_provider = new WeakMap();
_dom = new WeakMap();
_innerView = new WeakMap();
_updateValue = new WeakMap();
_app$2 = new WeakMap();
_onHide = new WeakMap();
_shouldShow = new WeakMap();

const mathInlineInputRule = $inputRule(
  (ctx) => nodeRule(/(?:\$)([^$]+)(?:\$)$/, mathInlineSchema.type(ctx), {
    getAttr: (match) => {
      var _a;
      return {
        value: (_a = match[1]) != null ? _a : ""
      };
    }
  })
);
const mathBlockInputRule = $inputRule(
  (ctx) => textblockTypeInputRule(/^\$\$[\s\n]$/, codeBlockSchema.type(ctx), () => ({
    language: "LaTeX"
  }))
);

const remarkMathPlugin = $remark(
  "remarkMath",
  () => remarkMath
);
function visitMathBlock(ast) {
  return visit(
    ast,
    "math",
    (node, index, parent) => {
      const { value } = node;
      const newNode = {
        type: "code",
        lang: "LaTeX",
        value
      };
      parent.children.splice(index, 1, newNode);
    }
  );
}
const remarkMathBlockPlugin = $remark(
  "remarkMathBlock",
  () => () => visitMathBlock
);

const latex = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.Latex)).config((ctx) => {
    const flags = useCrepeFeatures(ctx).get();
    const isCodeMirrorEnabled = flags.includes(CrepeFeature.CodeMirror);
    if (!isCodeMirrorEnabled) {
      throw new Error("You need to enable CodeMirror to use LaTeX feature");
    }
    ctx.update(codeBlockConfig.key, (prev) => ({
      ...prev,
      renderPreview: (language, content, applyPreview) => {
        if (language.toLowerCase() === "latex" && content.length > 0) {
          return renderLatex(content, config == null ? void 0 : config.katexOptions);
        }
        const renderPreview = prev.renderPreview;
        return renderPreview(language, content, applyPreview);
      }
    }));
    ctx.set(inlineLatexTooltip.key, {
      view: (view) => {
        var _a;
        return new LatexInlineTooltip(ctx, view, {
          inlineEditConfirm: (_a = config == null ? void 0 : config.inlineEditConfirm) != null ? _a : confirmIcon,
          ...config
        });
      }
    });
  }).use(remarkMathPlugin).use(remarkMathBlockPlugin).use(mathInlineSchema).use(inlineLatexTooltip).use(mathInlineInputRule).use(mathBlockInputRule).use(blockLatexSchema).use(toggleLatexCommand);
};
function renderLatex(content, options) {
  const html = katex.renderToString(content, {
    ...options,
    throwOnError: false,
    displayMode: true
  });
  return html;
}

const linkTooltip = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.LinkTooltip)).config(configureLinkTooltip).config((ctx) => {
    ctx.update(linkTooltipConfig.key, (prev) => {
      var _a, _b, _c, _d, _e, _f;
      return {
        ...prev,
        linkIcon: (_a = config == null ? void 0 : config.linkIcon) != null ? _a : copyIcon,
        editButton: (_b = config == null ? void 0 : config.editButton) != null ? _b : editIcon,
        removeButton: (_c = config == null ? void 0 : config.removeButton) != null ? _c : removeIcon,
        confirmButton: (_d = config == null ? void 0 : config.confirmButton) != null ? _d : confirmIcon,
        inputPlaceholder: (_e = config == null ? void 0 : config.inputPlaceholder) != null ? _e : "Paste link...",
        onCopyLink: (_f = config == null ? void 0 : config.onCopyLink) != null ? _f : (() => {
        })
      };
    });
  }).use(linkTooltipPlugin);
};

function configureListItem(ctx, config) {
  ctx.set(listItemBlockConfig.key, {
    renderLabel: ({ label, listType, checked }) => {
      var _a, _b, _c;
      if (checked == null) {
        if (listType === "bullet") return (_a = config == null ? void 0 : config.bulletIcon) != null ? _a : bulletIcon;
        return label;
      }
      if (checked) return (_b = config == null ? void 0 : config.checkBoxCheckedIcon) != null ? _b : checkBoxCheckedIcon;
      return (_c = config == null ? void 0 : config.checkBoxUncheckedIcon) != null ? _c : checkBoxUncheckedIcon;
    }
  });
}
const listItem = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.ListItem)).config((ctx) => configureListItem(ctx, config)).use(listItemBlockComponent);
};

function isDocEmpty(doc) {
  var _a;
  return doc.childCount <= 1 && !((_a = doc.firstChild) == null ? void 0 : _a.content.size);
}
function createPlaceholderDecoration(state, placeholderText) {
  const { selection } = state;
  if (!selection.empty) return null;
  const $pos = selection.$anchor;
  const node = $pos.parent;
  if (node.content.size > 0) return null;
  const inTable = findParent((node2) => node2.type.name === "table")($pos);
  if (inTable) return null;
  const before = $pos.before();
  return Decoration.node(before, before + node.nodeSize, {
    class: "crepe-placeholder",
    "data-placeholder": placeholderText
  });
}
const placeholderConfig = $ctx(
  {
    text: "Please enter...",
    mode: "block"
  },
  "placeholderConfigCtx"
);
const placeholderPlugin = $prose((ctx) => {
  return new Plugin({
    key: new PluginKey("CREPE_PLACEHOLDER"),
    props: {
      decorations: (state) => {
        var _a;
        const crepe = useCrepe(ctx);
        if (crepe.readonly) return null;
        const config = ctx.get(placeholderConfig.key);
        if (config.mode === "doc" && !isDocEmpty(state.doc)) return null;
        if (isInCodeBlock(state.selection) || isInList(state.selection))
          return null;
        const placeholderText = (_a = config.text) != null ? _a : "Please enter...";
        const deco = createPlaceholderDecoration(state, placeholderText);
        if (!deco) return null;
        return DecorationSet.create(state.doc, [deco]);
      }
    }
  });
});
const placeholder = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.Placeholder)).config((ctx) => {
    if (config) {
      ctx.update(placeholderConfig.key, (prev) => {
        return {
          ...prev,
          ...config
        };
      });
    }
  }).use(placeholderPlugin).use(placeholderConfig);
};

const table = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.Table)).config((ctx) => {
    ctx.update(tableBlockConfig.key, (defaultConfig) => ({
      ...defaultConfig,
      renderButton: (renderType) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _i;
        switch (renderType) {
          case "add_row":
            return (_a = config == null ? void 0 : config.addRowIcon) != null ? _a : plusIcon;
          case "add_col":
            return (_b = config == null ? void 0 : config.addColIcon) != null ? _b : plusIcon;
          case "delete_row":
            return (_c = config == null ? void 0 : config.deleteRowIcon) != null ? _c : removeIcon;
          case "delete_col":
            return (_d = config == null ? void 0 : config.deleteColIcon) != null ? _d : removeIcon;
          case "align_col_left":
            return (_e = config == null ? void 0 : config.alignLeftIcon) != null ? _e : alignLeftIcon;
          case "align_col_center":
            return (_f = config == null ? void 0 : config.alignCenterIcon) != null ? _f : alignCenterIcon;
          case "align_col_right":
            return (_g = config == null ? void 0 : config.alignRightIcon) != null ? _g : alignRightIcon;
          case "col_drag_handle":
            return (_h = config == null ? void 0 : config.colDragHandleIcon) != null ? _h : dragHandleIcon;
          case "row_drag_handle":
            return (_i = config == null ? void 0 : config.rowDragHandleIcon) != null ? _i : dragHandleIcon;
        }
      }
    }));
  }).use(tableBlock);
};

function getGroups$1(config, ctx) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const groupBuilder = new GroupBuilder();
  groupBuilder.addGroup("formatting", "Formatting").addItem("bold", {
    icon: (_a = config == null ? void 0 : config.boldIcon) != null ? _a : boldIcon,
    active: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      return commands.call(isMarkSelectedCommand.key, strongSchema.type(ctx2));
    },
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleStrongCommand.key);
    }
  }).addItem("italic", {
    icon: (_b = config == null ? void 0 : config.italicIcon) != null ? _b : italicIcon,
    active: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      return commands.call(
        isMarkSelectedCommand.key,
        emphasisSchema.type(ctx2)
      );
    },
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleEmphasisCommand.key);
    }
  }).addItem("strikethrough", {
    icon: (_c = config == null ? void 0 : config.strikethroughIcon) != null ? _c : strikethroughIcon,
    active: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      return commands.call(
        isMarkSelectedCommand.key,
        strikethroughSchema.type(ctx2)
      );
    },
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleStrikethroughCommand.key);
    }
  });
  const functionGroup = groupBuilder.addGroup("function", "Function");
  functionGroup.addItem("code", {
    icon: (_d = config == null ? void 0 : config.codeIcon) != null ? _d : codeIcon,
    active: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      return commands.call(
        isMarkSelectedCommand.key,
        inlineCodeSchema.type(ctx2)
      );
    },
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleInlineCodeCommand.key);
    }
  });
  const flags = ctx && useCrepeFeatures(ctx).get();
  const isLatexEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.Latex);
  if (isLatexEnabled) {
    functionGroup.addItem("latex", {
      icon: (_e = config == null ? void 0 : config.latexIcon) != null ? _e : functionsIcon,
      active: (ctx2) => {
        const commands = ctx2.get(commandsCtx);
        const nodeType = ctx2.get(schemaCtx).nodes[mathInlineId];
        return commands.call(isNodeSelectedCommand.key, nodeType);
      },
      onRun: (ctx2) => {
        const commands = ctx2.get(commandsCtx);
        commands.call(toggleLatexCommandName);
      }
    });
  }
  functionGroup.addItem("link", {
    icon: (_f = config == null ? void 0 : config.linkIcon) != null ? _f : linkIcon,
    active: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      return commands.call(isMarkSelectedCommand.key, linkSchema.type(ctx2));
    },
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleLinkCommand.key);
    }
  });
  if (ctx && (flags == null ? void 0 : flags.includes(CrepeFeature.AI))) {
    const aiCfg = ctx.get(aiProviderConfig.key);
    if (aiCfg.provider) {
      functionGroup.addItem("ai", {
        icon: (_h = (_g = config == null ? void 0 : config.aiIcon) != null ? _g : aiCfg.aiIcon) != null ? _h : aiIcon,
        active: () => false,
        onRun: (ctx2) => {
          const api = ctx2.get(aiInstructionTooltipAPI.key);
          const view = ctx2.get(editorViewCtx);
          const { from, to } = view.state.selection;
          api.show(from, to);
        }
      });
    }
  }
  (_i = config == null ? void 0 : config.buildToolbar) == null ? void 0 : _i.call(config, groupBuilder);
  return groupBuilder.build();
}

keepAlive(h, Fragment);
const Toolbar = defineComponent({
  props: {
    ctx: {
      type: Object,
      required: true
    },
    hide: {
      type: Function,
      required: true
    },
    show: {
      type: Object,
      required: true
    },
    selection: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: false
    }
  },
  setup(props) {
    const { ctx, config } = props;
    const onClick = (fn) => (e) => {
      e.preventDefault();
      if (ctx) {
        fn == null ? void 0 : fn(ctx);
      }
    };
    function checkActive(checker) {
      keepAlive(props.selection.value);
      const status = ctx.get(editorCtx).status;
      if (status !== EditorStatus.Created) return false;
      return checker(ctx);
    }
    const groupInfo = computed(() => getGroups$1(config, ctx));
    return () => {
      return /* @__PURE__ */ h(Fragment, null, groupInfo.value.map((group) => {
        return group.items.map((item) => {
          return /* @__PURE__ */ h(
            "button",
            {
              type: "button",
              class: clsx(
                "toolbar-item",
                ctx && checkActive(item.active) && "active"
              ),
              onPointerdown: onClick(item.onRun)
            },
            /* @__PURE__ */ h(Icon, { icon: item.icon })
          );
        });
      }).reduce((acc, curr, index) => {
        if (index === 0) {
          acc.push(...curr);
        } else {
          acc.push(/* @__PURE__ */ h("div", { class: "divider" }), ...curr);
        }
        return acc;
      }, []));
    };
  }
});

var __typeError$2 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$2 = (obj, member, msg) => member.has(obj) || __typeError$2("Cannot " + msg);
var __privateGet$2 = (obj, member, getter) => (__accessCheck$2(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$2 = (obj, member, value) => member.has(obj) ? __typeError$2("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$2 = (obj, member, value, setter) => (__accessCheck$2(obj, member, "write to private field"), member.set(obj, value), value);
var _tooltipProvider, _content, _app$1, _selection, _show;
const toolbarTooltip = tooltipFactory("CREPE_TOOLBAR");
class ToolbarView {
  constructor(ctx, view, config) {
    __privateAdd$2(this, _tooltipProvider);
    __privateAdd$2(this, _content);
    __privateAdd$2(this, _app$1);
    __privateAdd$2(this, _selection);
    __privateAdd$2(this, _show, ref(false));
    this.update = (view, prevState) => {
      __privateGet$2(this, _tooltipProvider).update(view, prevState);
      __privateGet$2(this, _selection).value = view.state.selection;
    };
    this.destroy = () => {
      __privateGet$2(this, _tooltipProvider).destroy();
      __privateGet$2(this, _app$1).unmount();
      __privateGet$2(this, _content).remove();
    };
    this.hide = () => {
      __privateGet$2(this, _tooltipProvider).hide();
    };
    const content = document.createElement("div");
    content.className = "milkdown-toolbar";
    __privateSet$2(this, _selection, shallowRef(view.state.selection));
    const app = createApp(Toolbar, {
      ctx,
      hide: this.hide,
      config,
      selection: __privateGet$2(this, _selection),
      show: __privateGet$2(this, _show)
    });
    app.mount(content);
    __privateSet$2(this, _content, content);
    __privateSet$2(this, _app$1, app);
    __privateSet$2(this, _tooltipProvider, new TooltipProvider({
      content: __privateGet$2(this, _content),
      debounce: 20,
      offset: 10,
      shouldShow(view2) {
        const { doc, selection } = view2.state;
        const { empty, from, to } = selection;
        const isEmptyTextBlock = !doc.textBetween(from, to).length && selection instanceof TextSelection;
        const isNotTextBlock = !(selection instanceof TextSelection);
        const activeElement = view2.dom.getRootNode().activeElement;
        const isTooltipChildren = content.contains(activeElement);
        const notHasFocus = !view2.hasFocus() && !isTooltipChildren;
        const isReadonly = !view2.editable;
        if (notHasFocus || isNotTextBlock || empty || isEmptyTextBlock || isReadonly)
          return false;
        return true;
      }
    }));
    __privateGet$2(this, _tooltipProvider).onShow = () => {
      __privateGet$2(this, _show).value = true;
    };
    __privateGet$2(this, _tooltipProvider).onHide = () => {
      __privateGet$2(this, _show).value = false;
    };
    this.update(view);
  }
}
_tooltipProvider = new WeakMap();
_content = new WeakMap();
_app$1 = new WeakMap();
_selection = new WeakMap();
_show = new WeakMap();
const toolbar = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.Toolbar)).config((ctx) => {
    ctx.set(toolbarTooltip.key, {
      view: (view) => new ToolbarView(ctx, view, config)
    });
  }).use(toolbarTooltip);
};

const defaultHeadingOptions = [
  { label: "Paragraph", level: null },
  { label: "Heading 1", level: 1 },
  { label: "Heading 2", level: 2 },
  { label: "Heading 3", level: 3 },
  { label: "Heading 4", level: 4 },
  { label: "Heading 5", level: 5 },
  { label: "Heading 6", level: 6 }
];
function getCurrentHeading(ctx, options) {
  var _a, _b;
  const headingOptions = options != null ? options : defaultHeadingOptions;
  const view = ctx.get(editorViewCtx);
  const { $from } = view.state.selection;
  const node = $from.parent;
  const paragraphOption = (_a = headingOptions.find((o) => o.level === null)) != null ? _a : headingOptions[0];
  if (node.type === headingSchema.type(ctx)) {
    const level = node.attrs.level;
    return (_b = headingOptions.find((o) => o.level === level)) != null ? _b : paragraphOption;
  }
  return paragraphOption;
}
function setHeading(ctx, level) {
  const commands = ctx.get(commandsCtx);
  if (level === null || level === void 0) {
    const paragraph = paragraphSchema.type(ctx);
    commands.call(setBlockTypeCommand.key, { nodeType: paragraph });
  } else {
    const heading = headingSchema.type(ctx);
    commands.call(setBlockTypeCommand.key, {
      nodeType: heading,
      attrs: { level }
    });
  }
}
function isMarkActive(ctx, markType) {
  const commands = ctx.get(commandsCtx);
  const selected = commands.call(isMarkSelectedCommand.key, markType);
  if (selected) return true;
  const view = ctx.get(editorViewCtx);
  const { state } = view;
  if (state.storedMarks) {
    return state.storedMarks.some((m) => m.type === markType);
  }
  if (state.selection instanceof TextSelection) {
    const { $cursor } = state.selection;
    if ($cursor) {
      return $cursor.marks().some((m) => m.type === markType);
    }
  }
  return false;
}
function buildHeadingSelector(headingOptions, chevronIcon) {
  const options = headingOptions != null ? headingOptions : defaultHeadingOptions;
  return {
    icon: "",
    active: () => false,
    selector: {
      chevronIcon: chevronIcon != null ? chevronIcon : chevronDownIcon,
      activeLabel: (ctx) => getCurrentHeading(ctx, options).label,
      options: options.map((opt) => ({
        label: opt.label,
        onSelect: (ctx) => setHeading(ctx, opt.level)
      }))
    }
  };
}
function getGroups(config, ctx) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
  const flags = ctx && useCrepeFeatures(ctx).get();
  const isLatexEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.Latex);
  const isImageBlockEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.ImageBlock);
  const isTableEnabled = flags == null ? void 0 : flags.includes(CrepeFeature.Table);
  const groupBuilder = new GroupBuilder();
  groupBuilder.addGroup("heading", "Heading").addItem("heading-selector", {
    ...buildHeadingSelector(config == null ? void 0 : config.headingOptions, config == null ? void 0 : config.chevronDownIcon)
  });
  groupBuilder.addGroup("formatting", "Formatting").addItem("bold", {
    icon: (_a = config == null ? void 0 : config.boldIcon) != null ? _a : boldIcon,
    active: (ctx2) => isMarkActive(ctx2, strongSchema.type(ctx2)),
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleStrongCommand.key);
    }
  }).addItem("italic", {
    icon: (_b = config == null ? void 0 : config.italicIcon) != null ? _b : italicIcon,
    active: (ctx2) => isMarkActive(ctx2, emphasisSchema.type(ctx2)),
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleEmphasisCommand.key);
    }
  }).addItem("strikethrough", {
    icon: (_c = config == null ? void 0 : config.strikethroughIcon) != null ? _c : strikethroughIcon,
    active: (ctx2) => isMarkActive(ctx2, strikethroughSchema.type(ctx2)),
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleStrikethroughCommand.key);
    }
  }).addItem("code", {
    icon: (_d = config == null ? void 0 : config.codeIcon) != null ? _d : codeIcon,
    active: (ctx2) => isMarkActive(ctx2, inlineCodeSchema.type(ctx2)),
    onRun: (ctx2) => {
      const view = ctx2.get(editorViewCtx);
      const { state } = view;
      if (state.selection.empty) {
        const markType = inlineCodeSchema.type(ctx2);
        const has = isMarkActive(ctx2, markType);
        if (has) {
          view.dispatch(state.tr.removeStoredMark(markType));
        } else {
          view.dispatch(state.tr.addStoredMark(markType.create()));
        }
      } else {
        const commands = ctx2.get(commandsCtx);
        commands.call(toggleInlineCodeCommand.key);
      }
    }
  });
  groupBuilder.addGroup("list", "List").addItem("bullet-list", {
    icon: (_e = config == null ? void 0 : config.bulletListIcon) != null ? _e : bulletListIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const bulletList = bulletListSchema.type(ctx2);
      commands.call(wrapInBlockTypeCommand.key, { nodeType: bulletList });
    }
  }).addItem("ordered-list", {
    icon: (_f = config == null ? void 0 : config.orderedListIcon) != null ? _f : orderedListIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const orderedList = orderedListSchema.type(ctx2);
      commands.call(wrapInBlockTypeCommand.key, { nodeType: orderedList });
    }
  }).addItem("task-list", {
    icon: (_g = config == null ? void 0 : config.taskListIcon) != null ? _g : todoListIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const listItem = listItemSchema.type(ctx2);
      commands.call(wrapInBlockTypeCommand.key, {
        nodeType: listItem,
        attrs: { checked: false }
      });
    }
  });
  const insertGroup = groupBuilder.addGroup("insert", "Insert");
  insertGroup.addItem("link", {
    icon: (_h = config == null ? void 0 : config.linkIcon) != null ? _h : linkIcon,
    active: (ctx2) => isMarkActive(ctx2, linkSchema.type(ctx2)),
    onRun: (ctx2) => {
      const view = ctx2.get(editorViewCtx);
      const { state } = view;
      const markType = linkSchema.type(ctx2);
      if (state.selection.empty && isMarkActive(ctx2, markType)) {
        view.dispatch(state.tr.removeStoredMark(markType));
        return;
      }
      const commands = ctx2.get(commandsCtx);
      commands.call(toggleLinkCommand.key);
    }
  });
  if (isImageBlockEnabled) {
    insertGroup.addItem("image", {
      icon: (_i = config == null ? void 0 : config.imageIcon) != null ? _i : imageIcon,
      active: () => false,
      onRun: (ctx2) => {
        const commands = ctx2.get(commandsCtx);
        const imageBlock = imageBlockSchema.type(ctx2);
        commands.call(addBlockTypeCommand.key, { nodeType: imageBlock });
      }
    });
  }
  if (isTableEnabled) {
    insertGroup.addItem("table", {
      icon: (_j = config == null ? void 0 : config.tableIcon) != null ? _j : tableIcon,
      active: () => false,
      onRun: (ctx2) => {
        const commands = ctx2.get(commandsCtx);
        const view = ctx2.get(editorViewCtx);
        const { from } = view.state.selection;
        commands.call(addBlockTypeCommand.key, {
          nodeType: createTable(ctx2, 3, 3)
        });
        commands.call(selectTextNearPosCommand.key, { pos: from });
      }
    });
  }
  const blockGroup = groupBuilder.addGroup("block", "Block");
  blockGroup.addItem("code-block", {
    icon: (_k = config == null ? void 0 : config.codeBlockIcon) != null ? _k : codeBlockIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const codeBlock = codeBlockSchema.type(ctx2);
      commands.call(setBlockTypeCommand.key, { nodeType: codeBlock });
    }
  });
  if (isLatexEnabled) {
    blockGroup.addItem("math", {
      icon: (_l = config == null ? void 0 : config.mathIcon) != null ? _l : functionsIcon,
      active: () => false,
      onRun: (ctx2) => {
        const commands = ctx2.get(commandsCtx);
        const codeBlock = codeBlockSchema.type(ctx2);
        commands.call(addBlockTypeCommand.key, {
          nodeType: codeBlock,
          attrs: { language: "LaTeX" }
        });
      }
    });
  }
  groupBuilder.addGroup("more", "More").addItem("quote", {
    icon: (_m = config == null ? void 0 : config.quoteIcon) != null ? _m : quoteIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const blockquote = blockquoteSchema.type(ctx2);
      commands.call(wrapInBlockTypeCommand.key, { nodeType: blockquote });
    }
  }).addItem("hr", {
    icon: (_n = config == null ? void 0 : config.hrIcon) != null ? _n : dividerIcon,
    active: () => false,
    onRun: (ctx2) => {
      const commands = ctx2.get(commandsCtx);
      const hr = hrSchema.type(ctx2);
      commands.call(addBlockTypeCommand.key, { nodeType: hr });
    }
  });
  (_o = config == null ? void 0 : config.buildTopBar) == null ? void 0 : _o.call(config, groupBuilder);
  return groupBuilder.build();
}

keepAlive(h, Fragment);
const TopBar = defineComponent({
  props: {
    ctx: {
      type: Object,
      required: true
    },
    version: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: false
    }
  },
  setup(props) {
    const { ctx, config } = props;
    const openSelectorKey = ref(null);
    const onClick = (fn) => (e) => {
      e.preventDefault();
      if (ctx) {
        fn(ctx);
      }
    };
    function isReady() {
      const status = ctx.get(editorCtx).status;
      return status === EditorStatus.Created;
    }
    function subscribeState() {
      keepAlive(props.version.value);
    }
    function checkActive(checker) {
      subscribeState();
      if (!isReady()) return false;
      return checker(ctx);
    }
    function getSelectorLabel(selector) {
      var _a, _b;
      subscribeState();
      if (!isReady()) return (_b = (_a = selector.options[0]) == null ? void 0 : _a.label) != null ? _b : "";
      return selector.activeLabel(ctx);
    }
    function onToggleSelector(key, e) {
      e.preventDefault();
      e.stopPropagation();
      openSelectorKey.value = openSelectorKey.value === key ? null : key;
    }
    const clickOutsideHandler = (e) => {
      const target = e.target;
      if (target.closest(".top-bar-heading-selector")) return;
      openSelectorKey.value = null;
    };
    onMounted(() => {
      window.addEventListener("pointerdown", clickOutsideHandler);
    });
    onUnmounted(() => {
      window.removeEventListener("pointerdown", clickOutsideHandler);
    });
    const groupInfo = computed(() => getGroups(config, ctx));
    function renderSelector(itemKey, selector) {
      const isOpen = openSelectorKey.value === itemKey;
      const activeLabel = getSelectorLabel(selector);
      return /* @__PURE__ */ h("div", { key: itemKey, class: "top-bar-heading-selector" }, /* @__PURE__ */ h(
        "button",
        {
          type: "button",
          class: "top-bar-heading-button",
          onPointerdown: (e) => onToggleSelector(itemKey, e)
        },
        /* @__PURE__ */ h("span", { class: "top-bar-heading-label" }, activeLabel),
        selector.chevronIcon && /* @__PURE__ */ h("span", { class: "top-bar-chevron" }, /* @__PURE__ */ h(Icon, { icon: selector.chevronIcon }))
      ), isOpen && /* @__PURE__ */ h("div", { class: "top-bar-heading-dropdown" }, selector.options.map((option, index) => /* @__PURE__ */ h(
        "button",
        {
          key: `${itemKey}-${index}`,
          type: "button",
          class: clsx(
            "top-bar-heading-option",
            activeLabel === option.label && "active"
          ),
          onPointerdown: (e) => {
            e.preventDefault();
            e.stopPropagation();
            openSelectorKey.value = null;
            option.onSelect(ctx);
          }
        },
        option.label
      ))));
    }
    function renderButton(item) {
      return /* @__PURE__ */ h(
        "button",
        {
          key: item.key,
          type: "button",
          class: clsx("top-bar-item", checkActive(item.active) && "active"),
          onPointerdown: onClick(item.onRun)
        },
        /* @__PURE__ */ h(Icon, { icon: item.icon })
      );
    }
    return () => {
      const view = isReady() ? ctx.get(editorViewCtx) : null;
      const isReadonly = view ? !view.editable : false;
      if (isReadonly) return null;
      return /* @__PURE__ */ h("div", { class: "top-bar-inner" }, groupInfo.value.map((group) => {
        return group.items.map((item) => {
          if (item.selector) {
            return renderSelector(item.key, item.selector);
          }
          if (!item.onRun) return null;
          return renderButton(
            item
          );
        });
      }).reduce((acc, curr, index) => {
        var _a, _b;
        if (index === 0) {
          acc.push(...curr);
        } else {
          const groupKey = (_b = (_a = groupInfo.value[index]) == null ? void 0 : _a.key) != null ? _b : index;
          acc.push(
            /* @__PURE__ */ h("div", { key: `divider-${groupKey}`, class: "top-bar-divider" }),
            ...curr
          );
        }
        return acc;
      }, []));
    };
  }
});

var __typeError$1 = (msg) => {
  throw TypeError(msg);
};
var __accessCheck$1 = (obj, member, msg) => member.has(obj) || __typeError$1("Cannot " + msg);
var __privateGet$1 = (obj, member, getter) => (__accessCheck$1(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd$1 = (obj, member, value) => member.has(obj) ? __typeError$1("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet$1 = (obj, member, value, setter) => (__accessCheck$1(obj, member, "write to private field"), member.set(obj, value), value);
var _container, _app, _version;
const topBarPluginKey = new PluginKey("CREPE_TOP_BAR");
class TopBarView {
  constructor(ctx, view, config) {
    __privateAdd$1(this, _container);
    __privateAdd$1(this, _app);
    __privateAdd$1(this, _version);
    this.update = (view, _prevState) => {
      __privateGet$1(this, _container).style.display = view.editable ? "" : "none";
      __privateGet$1(this, _version).value++;
    };
    this.destroy = () => {
      __privateGet$1(this, _app).unmount();
      __privateGet$1(this, _container).remove();
    };
    __privateSet$1(this, _version, ref(0));
    const container = document.createElement("div");
    container.className = "milkdown-top-bar";
    const app = createApp(TopBar, {
      ctx,
      config,
      version: __privateGet$1(this, _version)
    });
    app.mount(container);
    __privateSet$1(this, _container, container);
    __privateSet$1(this, _app, app);
    const editorRoot = view.dom.parentElement;
    if (editorRoot) {
      editorRoot.insertBefore(container, editorRoot.firstChild);
    }
    this.update(view);
  }
}
_container = new WeakMap();
_app = new WeakMap();
_version = new WeakMap();
const topBarSlice = $ctx(
  {
    view: () => ({
      update: () => {
      },
      destroy: () => {
      }
    })
  },
  "topBarConfig"
);
const topBarPlugin = $prose((ctx) => {
  const config = ctx.get(topBarSlice.key);
  return new Plugin({
    key: topBarPluginKey,
    view: config.view
  });
});
const topBar = (editor, config) => {
  editor.config(crepeFeatureConfig(CrepeFeature.TopBar)).config((ctx) => {
    ctx.set(topBarSlice.key, {
      view: (view) => new TopBarView(ctx, view, config)
    });
  }).use(topBarSlice).use(topBarPlugin);
};

function loadFeature(feature, editor, config) {
  switch (feature) {
    case CrepeFeature.CodeMirror: {
      return codeMirror(editor, config);
    }
    case CrepeFeature.ListItem: {
      return listItem(editor, config);
    }
    case CrepeFeature.LinkTooltip: {
      return linkTooltip(editor, config);
    }
    case CrepeFeature.ImageBlock: {
      return imageBlock(editor, config);
    }
    case CrepeFeature.Cursor: {
      return cursor(editor, config);
    }
    case CrepeFeature.BlockEdit: {
      return blockEdit(editor, config);
    }
    case CrepeFeature.Placeholder: {
      return placeholder(editor, config);
    }
    case CrepeFeature.Toolbar: {
      return toolbar(editor, config);
    }
    case CrepeFeature.Table: {
      return table(editor, config);
    }
    case CrepeFeature.Latex: {
      return latex(editor, config);
    }
    case CrepeFeature.TopBar: {
      return topBar(editor, config);
    }
    case CrepeFeature.AI: {
      return ai(editor, config);
    }
  }
}

var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var _editor, _rootElement, _editable;
class CrepeBuilder {
  /// The constructor of the crepe builder.
  /// You can pass configs to the builder to configure the editor.
  constructor({ root, defaultValue = "" } = {}) {
    /// @internal
    __privateAdd(this, _editor);
    /// @internal
    __privateAdd(this, _rootElement);
    /// @internal
    __privateAdd(this, _editable, true);
    /// Add a feature to the editor.
    this.addFeature = (feature, config) => {
      feature(__privateGet(this, _editor), config);
      return this;
    };
    /// Create the editor.
    this.create = () => {
      return __privateGet(this, _editor).create();
    };
    /// Destroy the editor.
    this.destroy = () => {
      return __privateGet(this, _editor).destroy();
    };
    /// Set the readonly mode of the editor.
    this.setReadonly = (value) => {
      __privateSet(this, _editable, !value);
      __privateGet(this, _editor).action((ctx) => {
        if (__privateGet(this, _editor).status === EditorStatus.Created) {
          const view = ctx.get(editorViewCtx);
          view.setProps({
            editable: () => !value
          });
        }
      });
      return this;
    };
    /// Get the markdown content of the editor.
    this.getMarkdown = () => {
      return __privateGet(this, _editor).action(getMarkdown());
    };
    /// Register event listeners.
    this.on = (fn) => {
      if (__privateGet(this, _editor).status !== EditorStatus.Created) {
        __privateGet(this, _editor).config((ctx) => {
          const listener2 = ctx.get(listenerCtx);
          fn(listener2);
        });
        return this;
      }
      __privateGet(this, _editor).action((ctx) => {
        const listener2 = ctx.get(listenerCtx);
        fn(listener2);
      });
      return this;
    };
    var _a;
    __privateSet(this, _rootElement, (_a = typeof root === "string" ? document.querySelector(root) : root) != null ? _a : document.body);
    __privateSet(this, _editor, Editor.make().config((ctx) => {
      ctx.inject(CrepeCtx, this);
      ctx.inject(FeaturesCtx, []);
    }).config((ctx) => {
      ctx.set(rootCtx, __privateGet(this, _rootElement));
      ctx.set(defaultValueCtx, defaultValue);
      ctx.set(editorViewOptionsCtx, {
        editable: () => __privateGet(this, _editable)
      });
      ctx.update(indentConfig.key, (value) => ({
        ...value,
        size: 4
      }));
      ctx.update(uploadConfig.key, (prev) => ({
        ...prev,
        uploader: async (files, schema, ctx2) => {
          const features = useCrepeFeatures(ctx2).get();
          const hasImageBlock = features.includes(CrepeFeature.ImageBlock);
          const nodeType = hasImageBlock ? schema.nodes["image-block"] : schema.nodes["image"];
          if (!nodeType) return [];
          const onUpload = hasImageBlock ? ctx2.get(imageBlockConfig.key).onUpload : void 0;
          const images = [];
          for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            if (file && file.type.includes("image")) images.push(file);
          }
          const nodes = await Promise.all(
            images.map(async (file) => {
              const src = onUpload ? await onUpload(file) : URL.createObjectURL(file);
              return nodeType.createAndFill({ src });
            })
          );
          return nodes;
        }
      }));
    }).use(commonmark).use(listener).use(history).use(indent).use(trailing).use(clipboard).use(upload).use(gfm));
  }
  /// Get the milkdown editor instance.
  get editor() {
    return __privateGet(this, _editor);
  }
  /// Get the readonly state of the editor.
  get readonly() {
    return !__privateGet(this, _editable);
  }
}
_editor = new WeakMap();
_rootElement = new WeakMap();
_editable = new WeakMap();

class Crepe extends CrepeBuilder {
  /// The constructor of the crepe editor.
  /// You can pass configs to the editor to configure the editor.
  /// Calling the constructor will not create the editor, you need to call `create` to create the editor.
  constructor({
    features = {},
    featureConfigs = {},
    ...crepeBuilderConfig
  } = {}) {
    super(crepeBuilderConfig);
    const finalConfigs = defaultsDeep(featureConfigs, defaultConfig);
    const enabledFeatures = Object.entries({
      ...defaultFeatures,
      ...features
    }).filter(([, enabled]) => enabled).map(([feature]) => feature);
    enabledFeatures.forEach((feature) => {
      const config = finalConfigs[feature];
      loadFeature(feature, this.editor, config);
    });
  }
}
/// This is an alias for the `CrepeFeature` enum.
Crepe.Feature = CrepeFeature;

export { Crepe, CrepeBuilder, CrepeFeature, useCrepe, useCrepeFeatures };
//# sourceMappingURL=index.js.map
